import {
  Activity,
  ArrowLeft,
  ArrowRight,
  CalendarDays,
  Check,
  CircleDollarSign,
  ClipboardList,
  FileText,
  Heart,
  LockKeyhole,
  MapPin,
  MessageCircle,
  RefreshCw,
  Scale,
  Search,
  Shield,
  UserRound,
  VenusAndMars,
  UsersRound
} from 'lucide-react';
import { useEffect, useRef, useState } from 'react';
import { createPlan, submitLead } from './api';
import advisorQrSrc from './assets/advisor-qr-b22bb047.jpg';
import GlobalLanding from './global/GlobalLanding';
import MenProgrammeLanding from './men/MenProgrammeLanding';
import { buildGuidanceReportForLocale, deriveRecommendedPathway, formatUSD, getValidationFlags, pathwayBadge } from './guidance';
import {
  babyGoalLabelsByLocale,
  carrierLabelsByLocale,
  categoryLabelsByLocale,
  countryStatusLabelsByLocale,
  eggSourceLabelsByLocale,
  getJourneyLocale,
  getLeadSource,
  getLocaleMeta,
  pathwayLabelsByLocale,
  spermSourceLabelsByLocale,
  stepTitlesByLocale,
  uiCopy,
  validationCopyByLocale
} from './journey-copy';
import type {
  AdminClient,
  BabyGoal,
  CarrierType,
  EggSource,
  EmbryoStatus,
  GenderPreference,
  GuidanceReport,
  JourneyLocale,
  LeadRegistration,
  MedicalInputs,
  SpermSource,
  UserCategory,
  UserPreferences
} from './types';

type Step = 'intro' | 'goal' | 'category' | 'paths' | 'review' | 'medical' | 'report';
type JourneyPage = 'home' | 'selection' | 'recommendation';
type ReportTab = 'legality' | 'pathway' | 'medical' | 'cost' | 'timeline' | 'risk' | 'register';

const initialPreferences: UserPreferences = {
  category: null,
  babyGoal: 'healthy_singleton',
  sexSelectionRequested: false,
  desiredSex: null,
  singletonPreferred: true,
  twinPreference: false,
  twinType: null,
  requiresMedicalReview: false,
  existingEmbryo: false,
  goalUnknown: false,
  genderPreference: 'none',
  eggSource: 'self',
  spermSource: 'partner',
  carrier: 'self',
  embryoStatus: 'none',
  recommendedPathway: 'ivf',
  validationFlags: []
};

const categoryDefaults: Record<UserCategory, Partial<UserPreferences>> = {
  spouses: { eggSource: 'self', spermSource: 'partner', carrier: 'self', embryoStatus: 'none', genderPreference: 'none' },
  single_woman: { eggSource: 'self', spermSource: 'donor', carrier: 'self', embryoStatus: 'none', genderPreference: 'none' },
  single_man: { eggSource: 'donor', spermSource: 'self', carrier: 'surrogate', embryoStatus: 'none', genderPreference: 'none' },
  female_couple: { eggSource: 'self', spermSource: 'donor', carrier: 'partner', embryoStatus: 'none', genderPreference: 'none' },
  male_couple: { eggSource: 'donor', spermSource: 'self', carrier: 'surrogate', embryoStatus: 'none', genderPreference: 'none' },
  other_lgbtq: { eggSource: 'unknown', spermSource: 'unknown', carrier: 'unknown', embryoStatus: 'unknown', genderPreference: 'none' }
};

const initialMedical: MedicalInputs = {
  age: null,
  amh: null,
  fsh: null,
  afc: null,
  spermConcentration: null,
  spermMotility: 'unknown',
  spermMorphology: null,
  uterusStatus: 'unknown',
  endometriumStatus: 'unknown',
  previousCycles: 0,
  previousMiscarriage: false,
  previousFailedTransfer: false,
  embryoCount: null,
  embryoFreezeLocation: '',
  embryoTransportReady: false,
  medicalNotes: ''
};

const categoryIcons: Record<UserCategory, typeof UsersRound> = {
  spouses: UsersRound,
  single_woman: UserRound,
  single_man: UserRound,
  female_couple: Heart,
  male_couple: VenusAndMars,
  other_lgbtq: Heart
};

const categoryBackgrounds: Record<UserCategory, string> = {
  spouses: '/profile-spouses.png',
  single_woman: '/profile-single-woman.png',
  single_man: '/profile-single-man.png',
  female_couple: '/profile-female-couple.png',
  male_couple: '/profile-male-couple.png',
  other_lgbtq: '/profile-lgbtq.png'
};

const reportTabs: Array<{ id: ReportTab; icon: typeof Scale }> = [
  { id: 'legality', icon: Scale },
  { id: 'pathway', icon: ClipboardList },
  { id: 'medical', icon: Activity },
  { id: 'cost', icon: CircleDollarSign },
  { id: 'risk', icon: Shield },
  { id: 'timeline', icon: CalendarDays },
  { id: 'register', icon: FileText }
];

function goalToPreferences(goal: BabyGoal): Partial<UserPreferences> {
  const base: Partial<UserPreferences> = {
    babyGoal: goal,
    sexSelectionRequested: false,
    desiredSex: null,
    singletonPreferred: true,
    twinPreference: false,
    twinType: null,
    requiresMedicalReview: false,
    existingEmbryo: false,
    goalUnknown: false,
    genderPreference: 'none'
  };

  if (goal === 'male_singleton') return { ...base, sexSelectionRequested: true, desiredSex: 'male', genderPreference: 'son' };
  if (goal === 'female_singleton') return { ...base, sexSelectionRequested: true, desiredSex: 'female', genderPreference: 'daughter' };
  if (goal === 'twins_boy_girl') return { ...base, sexSelectionRequested: true, singletonPreferred: false, twinPreference: true, twinType: 'boy_girl', requiresMedicalReview: true, genderPreference: 'twins' };
  if (goal === 'twins_boy_boy') return { ...base, sexSelectionRequested: true, desiredSex: 'male', singletonPreferred: false, twinPreference: true, twinType: 'boy_boy', requiresMedicalReview: true, genderPreference: 'twins' };
  if (goal === 'twins_girl_girl') return { ...base, sexSelectionRequested: true, desiredSex: 'female', singletonPreferred: false, twinPreference: true, twinType: 'girl_girl', requiresMedicalReview: true, genderPreference: 'twins' };
  if (goal === 'existing_embryo_transfer') return { ...base, existingEmbryo: true, eggSource: 'existing_embryo', embryoStatus: 'unknown' };
  if (goal === 'unknown') return { ...base, singletonPreferred: false, goalUnknown: true };
  return base;
}

function withDerivedPreferences(preferences: UserPreferences, medical?: MedicalInputs): UserPreferences {
  return {
    ...preferences,
    recommendedPathway: deriveRecommendedPathway(preferences, medical),
    validationFlags: getValidationFlags(preferences, medical)
  };
}

function withCategoryDefaults(preferences: UserPreferences, category: UserCategory): UserPreferences {
  const next = { ...preferences, category, ...categoryDefaults[category], ...goalToPreferences(preferences.babyGoal) };
  if (preferences.existingEmbryo) {
    next.eggSource = 'existing_embryo';
    next.embryoStatus = 'unknown';
  }
  return withDerivedPreferences(next);
}

function App() {
  const isOperations = window.location.pathname.startsWith('/we');
  const menProgrammeHosts = new Set([
    'en.ivftoday.org', 'www.en.ivftoday.org',
    'de.ivftoday.org', 'www.de.ivftoday.org',
    'jp.ivftoday.org', 'www.jp.ivftoday.org',
    'kr.ivftoday.org', 'www.kr.ivftoday.org'
  ]);
  const legacyJourneyHosts = new Set(['vn.ivftoday.org', 'www.vn.ivftoday.org', 'lgbtq.ivftoday.org', 'www.lgbtq.ivftoday.org']);
  const isMenProgrammeHost = menProgrammeHosts.has(window.location.hostname) || window.location.pathname === '/men-programme-preview';
  const isLegacyJourneyHost = legacyJourneyHosts.has(window.location.hostname);
  if (isOperations) return <AdminPortal />;
  if (isMenProgrammeHost) return <MenProgrammeLanding />;
  if (isLegacyJourneyHost) return <LegacyJourneyApp />;
  return <GlobalLanding />;
}

function LegacyJourneyApp() {
  const locale = getJourneyLocale();
  const [page, setPage] = useState<JourneyPage>(() => {
    const start = new URLSearchParams(window.location.search).get('start');
    return start === 'category' || start === 'selection' ? 'selection' : 'home';
  });
  const [preferences, setPreferences] = useState<UserPreferences>(initialPreferences);
  const [goalConfirmed, setGoalConfirmed] = useState(false);
  const [medical, setMedical] = useState<MedicalInputs>(initialMedical);
  const [report, setReport] = useState<GuidanceReport>(() => buildGuidanceReportForLocale(initialPreferences, initialMedical, locale));
  const mainRef = useRef<HTMLElement>(null);

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'auto' });
  }, [page]);

  const runReport = async () => {
    const normalized = withDerivedPreferences(preferences, medical);
    setPreferences(normalized);
    const localReport = buildGuidanceReportForLocale(normalized, medical, locale);
    setReport(localReport);
    setPage('recommendation');
    setReport(await createPlan(normalized, medical, locale));
  };

  return (
    <div className={`app-shell ${page === 'home' ? 'landing-shell' : 'journey-shell'}`}>
      <a className="skip-link" href="#main-content">Bỏ qua đến nội dung chính</a>
      <main id="main-content" ref={mainRef} tabIndex={-1}>
        {page === 'home' ? (
          <IntroPoster locale={locale} onStart={() => setPage('selection')} onSelectAudience={(category) => {
            setPreferences(withCategoryDefaults(preferences, category));
            setPage('selection');
          }} />
        ) : null}
        {page === 'selection' ? (
          <SelectionPage locale={locale} preferences={preferences} setPreferences={setPreferences} goalConfirmed={goalConfirmed} setGoalConfirmed={setGoalConfirmed} medical={medical} setMedical={setMedical} onHome={() => setPage('home')} onRecommend={() => void runReport()} />
        ) : null}
        {page === 'recommendation' ? (
          <RecommendationPage locale={locale} preferences={preferences} report={report} onEdit={() => setPage('selection')} onHome={() => {
            setPreferences(initialPreferences);
            setGoalConfirmed(false);
            setMedical(initialMedical);
            setPage('home');
          }} />
        ) : null}
      </main>
    </div>
  );
}

function TopProgress({ locale, step, setStep }: { locale: JourneyLocale; step: Step; setStep: (step: Step) => void }) {
  const steps: Step[] = ['category', 'paths', 'medical', 'report'];
  const currentIndex = steps.indexOf(step);
  const copy = uiCopy[locale];
  const stepTitles = stepTitlesByLocale[locale];

  return (
    <header className="top-progress">
      <div>
        <div className="micro">{copy.progressKicker}</div>
        <h1>{stepTitles[step]}</h1>
      </div>
      <nav aria-label={copy.progressAria}>
        {steps.map((item, index) => (
          <button
            key={item}
            type="button"
            className={index <= currentIndex ? 'active' : ''}
            onClick={() => index <= currentIndex && setStep(item)}
          >
            <span>0{index + 1}</span>
            {stepTitles[item]}
          </button>
        ))}
      </nav>
    </header>
  );
}

function IntroPoster({
  locale,
  onStart,
  onSelectAudience
}: {
  locale: JourneyLocale;
  onStart: () => void;
  onSelectAudience: (category: 'male_couple' | 'single_man') => void;
}) {
  const { intro } = uiCopy[locale];
  const text = locale === 'zh-Hans'
    ? {
        brandLine: 'FATHERHOOD PLANNING',
        navPath: '路径',
        navSolution: '方案',
        navPrice: '费用',
        heroKicker: 'FOR GAY MEN + SINGLE INTENDED FATHERS',
        heroBefore: '每一种父爱，',
        heroAccent: '都值得一条清晰而安心的路',
        heroCopy: '用三步排除不适合的国家与方案，再把医疗、法律和预算交给专业人员逐项复核。',
        trust: ['私密填写', '不承诺结果', '医疗与法律复核'],
        pathMicro: 'YOUR DECISION MAP',
        pathTitle: '你的三步路径',
        pathSteps: [
          ['01', '确认身份', '男同性伴侣或单身准爸爸'],
          ['02', '组合方案', '精子、供卵、胚胎与孕母'],
          ['03', '核对国家', '准入、亲权、时间与总预算']
        ],
        quoteStart: '国际父亲路径参考预算起点',
        quoteStartNote: '先看方向，再由专业团队确认适合你的国家与完整方案。',
        solutionMicro: 'SOLUTION',
        solutionTitle: '先选身份，再生成方案',
        solutionCopy: '两条路径需要相同的生殖资源，但亲权文件、精子选择与风险审查不同。',
        coupleTitle: '男同性伴侣',
        coupleEn: 'MALE COUPLE',
        coupleCopy: '确认使用哪一方精子、是否建立两组胚胎，以及两位父亲的亲权文件。',
        couplePoints: ['一方或双方精子', '供卵与胚胎建立', '孕母与双亲亲权审查'],
        singleTitle: '单身准爸爸',
        singleEn: 'SINGLE INTENDED FATHER',
        singleCopy: '以个人精子、供卵、孕母和单身父亲亲权为核心，核对可进入的国家。',
        singlePoints: ['个人精子', '供卵与胚胎建立', '孕母与单身亲权审查'],
        select: '生成我的路径',
        priceMicro: 'COUNTRY ADVANTAGES',
        priceTitle: '比较各国优势与预算',
        priceCopy: '预算效率、服务整合、亲权规划与专业资源各有侧重。先找到更符合家庭目标的方向，再进入个案复核。',
        armeniaBadge: '预算效率优势',
        georgiaBadge: '服务整合优势',
        kyrgyzstanBadge: '医疗衔接优势',
        usaBadge: '专业体系优势',
        armenia: '亚美尼亚',
        armeniaCopy: '适合重视预算效率的家庭：套餐起点较低，医疗、孕母协调与阶段性付款可在同一方案框架内规划。',
        georgia: '格鲁吉亚',
        georgiaCopy: '适合希望流程更集中、沟通更省心的家庭：供卵、胚胎与孕母协作可由同一服务链统筹推进。',
        kyrgyzstan: '吉尔吉斯斯坦',
        kyrgyzstanCopy: '适合重视医疗流程衔接的家庭：促排、胚胎培养与移植安排强调一体化，减少跨团队协调压力。',
        usa: '美国',
        usaCopy: '适合重视专业分工与亲权规划的家庭：可按家庭情况组合诊所、供卵、孕母机构与律师团队。',
        verifyLabel: '选择前必须复核',
        verifyItems: ['当前身份准入与当地法律', '医疗包含项、排除项与付款节点', '出生亲权、旅行证件与回国路径'],
        sourceNote: '价格仅作预算起点。选择国家前，请完成医疗、法律与亲权文件复核。',
        answersMicro: 'FATHERHOOD PLANNING ANSWERS',
        answersTitle: '男同性伴侣与单身男性，如何开始跨境父亲路径？',
        answersCopy: '先回答三个关键问题，再比较国家、方案与价格。以下内容用于建立决策顺序，不替代医生或律师的个案意见。',
        answers: [
          ['先选择国家，还是先确定生育方案？', '先确认身份准入、配子来源、孕母安排与亲权目标，再比较国家。仅按价格排序，容易遗漏律师、保险、差旅和出生文件。'],
          ['跨境代孕预算通常包含哪些费用？', '至少应拆分为 IVF 与胚胎实验室、供卵、孕母、法律与亲权、保险、机构管理、差旅和出生后文件。'],
          ['什么时候需要医生和律师进行专业复核？', '在支付不可退订金、签署孕母合同或开始医疗前，应由诊所和律师书面确认身份准入、医疗范围、付款节点与亲权文件。']
        ],
        privacyTitle: '先获得清晰答案，再决定是否留下联系方式。',
        privacyCopy: '初步评估不要求公开你的故事；只有你主动申请顾问复核时，资料才进入联系流程。',
        ctaTitle: '获得与你身份匹配的路径与预算',
        ctaCopy: '约 3 分钟。先筛选，再复核。'
      }
    : locale === 'vi'
      ? {
          brandLine: 'FATHERHOOD PLANNING',
          navPath: 'Lộ trình',
          navSolution: 'Giải pháp',
          navPrice: 'Chi phí',
          heroKicker: 'FOR GAY MEN + SINGLE INTENDED FATHERS',
          heroBefore: 'Làm cha bắt đầu bằng việc hiểu rõ ',
          heroAccent: 'lộ trình, giải pháp và chi phí.',
          heroCopy: 'Ba bước để loại bỏ lựa chọn không phù hợp, sau đó rà soát y khoa, pháp lý và ngân sách với chuyên gia.',
          trust: ['Riêng tư', 'Không hứa kết quả', 'Rà soát y khoa và pháp lý'],
          pathMicro: 'YOUR DECISION MAP',
          pathTitle: 'Lộ trình ba bước',
          pathSteps: [
            ['01', 'Xác nhận hồ sơ', 'Cặp đôi nam hoặc cha đơn thân'],
            ['02', 'Lập giải pháp', 'Tinh trùng, trứng hiến, phôi và người mang thai'],
            ['03', 'Kiểm tra quốc gia', 'Tiếp cận, quyền cha mẹ, thời gian và ngân sách']
          ],
          quoteStart: 'Ngân sách tham khảo cho lộ trình quốc tế bắt đầu từ',
          quoteStartNote: 'Xem định hướng trước, sau đó xác nhận quốc gia và phương án phù hợp với đội ngũ chuyên môn.',
          solutionMicro: 'SOLUTION',
          solutionTitle: 'Chọn hồ sơ để tạo giải pháp',
          solutionCopy: 'Hai lộ trình dùng nguồn sinh sản tương tự nhưng khác về lựa chọn tinh trùng, giấy tờ và rà soát rủi ro.',
          coupleTitle: 'Cặp đôi nam',
          coupleEn: 'MALE COUPLE',
          coupleCopy: 'Xác định nguồn tinh trùng, một hay hai nhóm phôi và giấy tờ cho cả hai người cha.',
          couplePoints: ['Tinh trùng của một hoặc cả hai', 'Trứng hiến và tạo phôi', 'Người mang thai và quyền của hai cha'],
          singleTitle: 'Nam độc thân muốn làm cha',
          singleEn: 'SINGLE INTENDED FATHER',
          singleCopy: 'Kiểm tra quốc gia phù hợp với tinh trùng cá nhân, trứng hiến, người mang thai và quyền cha đơn thân.',
          singlePoints: ['Tinh trùng cá nhân', 'Trứng hiến và tạo phôi', 'Người mang thai và quyền cha đơn thân'],
          select: 'Tạo lộ trình của tôi',
          priceMicro: 'COUNTRY ADVANTAGES',
          priceTitle: 'So sánh lợi thế và ngân sách',
          priceCopy: 'Mỗi quốc gia có thế mạnh riêng về ngân sách, phối hợp dịch vụ, kế hoạch quyền cha mẹ và nguồn lực chuyên môn.',
          armeniaBadge: 'Lợi thế ngân sách',
          georgiaBadge: 'Lợi thế phối hợp',
          kyrgyzstanBadge: 'Lợi thế y khoa',
          usaBadge: 'Hệ thống chuyên môn',
          armenia: 'Armenia',
          armeniaCopy: 'Phù hợp với gia đình ưu tiên hiệu quả ngân sách, với y khoa, phối hợp người mang thai và các mốc thanh toán được lập trong cùng một khung kế hoạch.',
          georgia: 'Georgia',
          georgiaCopy: 'Phù hợp với gia đình muốn quy trình tập trung hơn, khi trứng hiến, phôi và phối hợp người mang thai có thể được quản lý trong một chuỗi dịch vụ.',
          kyrgyzstan: 'Kyrgyzstan',
          kyrgyzstanCopy: 'Phù hợp với gia đình ưu tiên sự liền mạch y khoa, với kích thích, nuôi phôi và chuyển phôi được tổ chức theo hướng tích hợp.',
          usa: 'Hoa Kỳ',
          usaCopy: 'Phù hợp với gia đình coi trọng phân công chuyên môn và kế hoạch quyền cha mẹ, với nhiều lựa chọn để ghép phòng khám, nguồn trứng, đơn vị người mang thai và luật sư.',
          verifyLabel: 'Phải kiểm tra trước khi chọn',
          verifyItems: ['Điều kiện tiếp cận và luật hiện hành', 'Phạm vi y khoa, loại trừ và mốc thanh toán', 'Quyền cha mẹ, giấy tờ đi lại và trở về'],
          sourceNote: 'Giá chỉ là điểm khởi đầu ngân sách. Hãy rà soát y khoa, pháp lý và hồ sơ quyền cha mẹ trước khi chọn quốc gia.',
          answersMicro: 'FATHERHOOD PLANNING ANSWERS',
          answersTitle: 'Cặp đôi nam và nam độc thân nên bắt đầu lộ trình làm cha xuyên biên giới như thế nào?',
          answersCopy: 'Trả lời ba câu hỏi cốt lõi trước khi so sánh quốc gia, giải pháp và chi phí. Nội dung này sắp xếp thứ tự quyết định, không thay thế tư vấn cá nhân của bác sĩ hoặc luật sư.',
          answers: [
            ['Nên chọn quốc gia trước hay xác định phương án sinh sản trước?', 'Trước tiên hãy xác nhận điều kiện hồ sơ, nguồn giao tử, người mang thai và mục tiêu quyền cha mẹ. Nếu chỉ xếp hạng theo giá, bạn dễ bỏ sót luật sư, bảo hiểm, đi lại và giấy tờ khai sinh.'],
            ['Ngân sách mang thai hộ xuyên biên giới thường gồm những gì?', 'Ít nhất nên tách IVF và phòng lab phôi, trứng hiến, người mang thai, pháp lý và quyền cha mẹ, bảo hiểm, quản lý, đi lại và hồ sơ sau sinh.'],
            ['Khi nào cần bác sĩ và luật sư rà soát?', 'Trước khi trả khoản đặt cọc không hoàn lại, ký hợp đồng với người mang thai hoặc bắt đầu điều trị, hãy yêu cầu phòng khám và luật sư xác nhận bằng văn bản về điều kiện, phạm vi y khoa, mốc thanh toán và hồ sơ quyền cha mẹ.']
          ],
          privacyTitle: 'Có câu trả lời rõ ràng trước khi để lại thông tin.',
          privacyCopy: 'Đánh giá ban đầu không yêu cầu kể công khai câu chuyện; dữ liệu chỉ vào luồng liên hệ khi bạn yêu cầu chuyên gia rà soát.',
          ctaTitle: 'Nhận lộ trình và ngân sách phù hợp',
          ctaCopy: 'Khoảng 3 phút. Lọc trước, rà soát sau.'
        }
      : {
          brandLine: 'FATHERHOOD PLANNING',
          navPath: 'Path',
          navSolution: 'Solution',
          navPrice: 'Price',
          heroKicker: 'FOR GAY MEN + SINGLE INTENDED FATHERS',
          heroBefore: 'See the ',
          heroAccent: 'path, solution, and price',
          heroAfter: ' before you choose.',
          heroCopy: 'Use three decisions to remove unsuitable options, then verify the medical, legal, and budget details with professionals.',
          trust: ['Private by design', 'No outcome promises', 'Medical and legal review'],
          pathMicro: 'YOUR DECISION MAP',
          pathTitle: 'Your three-step path',
          pathSteps: [
            ['01', 'Confirm your profile', 'Gay male couple or single intended father'],
            ['02', 'Build the solution', 'Sperm, donor eggs, embryos, and carrier'],
            ['03', 'Check the country', 'Access, parentage, timing, and total budget']
          ],
          quoteStart: 'Reference budget for international fatherhood paths starts at',
          quoteStartNote: 'Use this as direction, then confirm the country and complete plan with the professional team.',
          solutionMicro: 'SOLUTION',
          solutionTitle: 'Choose your profile. Build the solution.',
          solutionCopy: 'The reproductive resources are similar, but sperm choices, parentage documents, and risk review are different.',
          coupleTitle: 'Gay male couple',
          coupleEn: 'MALE COUPLE',
          coupleCopy: 'Decide whose sperm to use, whether to create one or two embryo cohorts, and how both fathers establish parentage.',
          couplePoints: ['One or both partners’ sperm', 'Donor eggs and embryo creation', 'Carrier and two-father parentage review'],
          singleTitle: 'Single intended father',
          singleEn: 'SINGLE INTENDED FATHER',
          singleCopy: 'Check workable countries around your sperm, donor eggs, a gestational carrier, and single-father parentage.',
          singlePoints: ['Your sperm', 'Donor eggs and embryo creation', 'Carrier and single-father parentage review'],
          select: 'Build my path',
          priceMicro: 'COUNTRY ADVANTAGES',
          priceTitle: 'Compare advantages and budgets',
          priceCopy: 'Each country emphasizes a different mix of budget efficiency, service coordination, parentage planning, and professional resources.',
          armeniaBadge: 'Budget efficiency',
          georgiaBadge: 'Service coordination',
          kyrgyzstanBadge: 'Medical continuity',
          usaBadge: 'Professional ecosystem',
          armenia: 'Armenia',
          armeniaCopy: 'Suited to families prioritizing budget efficiency, with medical care, carrier coordination, and staged payments planned within one program framework.',
          georgia: 'Georgia',
          georgiaCopy: 'Suited to families seeking a more coordinated process, with donor eggs, embryos, and carrier support managed through one service chain.',
          kyrgyzstan: 'Kyrgyzstan',
          kyrgyzstanCopy: 'Suited to families prioritizing medical continuity, with stimulation, embryo culture, and transfer organized as an integrated workflow.',
          usa: 'United States',
          usaCopy: 'Suited to families prioritizing specialist choice and parentage planning, with flexible combinations of clinics, donor-egg providers, carrier agencies, and legal teams.',
          verifyLabel: 'Verify before choosing',
          verifyItems: ['Current access rules and local law', 'Medical inclusions, exclusions, and payment milestones', 'Parentage, travel documents, and return-home route'],
          sourceNote: 'Prices are budget starting points. Verify the medical plan, legal pathway, and parentage documents before choosing a country.',
          answersMicro: 'FATHERHOOD PLANNING ANSWERS',
          answersTitle: 'How should gay male couples and single men begin a cross-border fatherhood plan?',
          answersCopy: 'Answer three core questions before comparing countries, solutions, and prices. This establishes a decision order and does not replace case-specific medical or legal advice.',
          answers: [
            ['Should you choose a country or define the reproductive plan first?', 'Confirm profile access, gamete sources, carrier arrangements, and parentage goals before comparing countries. Ranking by price alone can miss legal, insurance, travel, and birth-document costs.'],
            ['What should a cross-border surrogacy budget include?', 'At minimum, separate IVF and embryology, donor eggs, the gestational carrier, legal and parentage work, insurance, agency management, travel, and post-birth documents.'],
            ['When should a physician and attorney review the plan?', 'Before paying a non-refundable deposit, signing a carrier agreement, or starting treatment, obtain written confirmation of access, medical scope, payment milestones, and parentage documents.']
          ],
          privacyTitle: 'Get clarity before you share contact details.',
          privacyCopy: 'The initial assessment does not ask you to publish your story. Details enter the contact flow only when you request advisor review.',
          ctaTitle: 'Get a path and budget matched to your profile',
          ctaCopy: 'About 3 minutes. Filter first, verify next.'
        };

  const heroAfter = 'heroAfter' in text ? text.heroAfter : '';
  const priceCards = [
    { country: text.armenia, amount: '$78,800', description: text.armeniaCopy, badge: text.armeniaBadge },
    { country: text.georgia, amount: '$81,800', description: text.georgiaCopy, badge: text.georgiaBadge },
    { country: text.kyrgyzstan, amount: '$102,000', description: text.kyrgyzstanCopy, badge: text.kyrgyzstanBadge },
    { country: text.usa, amount: '$202K–$322K', description: text.usaCopy, badge: text.usaBadge }
  ];

  return (
    <section className="decision-landing">
      <header className="decision-nav">
        <a className="fatherhood-brand" href="/" aria-label="IVFToday home">
          <img src="/brand/ivftoday-logo-640.png" alt="IVFToday" width="640" height="270" />
          <small>{text.brandLine}</small>
        </a>
        <nav aria-label="Primary navigation">
          <a href="#path">{text.navPath}</a>
          <a href="#solution">{text.navSolution}</a>
          <a href="#price">{text.navPrice}</a>
        </nav>
        <button type="button" onClick={onStart}>{intro.start} <ArrowRight size={17} /></button>
      </header>

      <section className="decision-hero" id="path" aria-labelledby="fatherhood-title">
        <div className="decision-hero-copy">
          <p className="sport-kicker">{text.heroKicker}</p>
          <h1 id="fatherhood-title" tabIndex={-1}>
            {text.heroBefore}<span>{text.heroAccent}</span>{heroAfter}
          </h1>
          <p className="decision-lead">{text.heroCopy}</p>
          <div className="decision-actions">
            <button className="fatherhood-primary" onClick={onStart} type="button">
              {intro.start}<ArrowRight size={20} />
            </button>
            <a href="#price">{text.navPrice}<ArrowRight size={16} /></a>
          </div>
          <ul className="decision-trust" aria-label="Planning boundaries">
            {text.trust.map((item) => <li key={item}><Check size={15} />{item}</li>)}
          </ul>
        </div>

        <figure className="decision-hero-photo">
          <picture>
            <source media="(max-width: 760px)" srcSet="/hero-fatherhood-men-v1-960.webp" />
            <img
              src="/hero-fatherhood-men-v1.webp"
              width="1774"
              height="887"
              fetchPriority="high"
              decoding="async"
              alt={locale === 'zh-Hans' ? '两位亚洲父亲在家中抱着婴儿' : locale === 'vi' ? 'Hai người cha châu Á ôm em bé tại nhà' : 'Two Asian fathers holding their baby at home'}
            />
          </picture>
          <figcaption>{locale === 'zh-Hans' ? '先看见家庭，再规划路径。' : locale === 'vi' ? 'Thấy gia đình trước, rồi lập lộ trình.' : 'See the family first. Then plan the path.'}</figcaption>
        </figure>

        <aside className="decision-map decision-map-wide" aria-label={text.pathTitle}>
          <header>
            <p>{text.pathMicro}</p>
            <h2>{text.pathTitle}</h2>
          </header>
          <ol>
            {text.pathSteps.map(([number, title, copy]) => (
              <li key={number}>
                <span>{number}</span>
                <div><strong>{title}</strong><small>{copy}</small></div>
              </li>
            ))}
          </ol>
          <div className="decision-map-price">
            <small>{text.quoteStart}</small>
            <strong>$78,800</strong>
            <p>{text.quoteStartNote}</p>
          </div>
        </aside>
      </section>

      <section className="solution-section" id="solution">
        <header className="decision-section-heading">
          <p className="sport-kicker">{text.solutionMicro}</p>
          <h2>{text.solutionTitle}</h2>
          <p>{text.solutionCopy}</p>
        </header>
        <div className="solution-grid">
          <button className="solution-card" type="button" onClick={() => onSelectAudience('male_couple')}>
            <span className="solution-card-photo" aria-hidden="true">
              <img src="/hero-fatherhood-men-v1-960.webp" alt="" loading="lazy" decoding="async" />
            </span>
            <span className="solution-icon"><UsersRound size={25} /></span>
            <small>{text.coupleEn}</small>
            <strong>{text.coupleTitle}</strong>
            <p>{text.coupleCopy}</p>
            <ul>{text.couplePoints.map((item) => <li key={item}><Check size={15} />{item}</li>)}</ul>
            <b>{text.select}<ArrowRight size={18} /></b>
          </button>
          <button className="solution-card solution-card-light" type="button" onClick={() => onSelectAudience('single_man')}>
            <span className="solution-card-photo" aria-hidden="true">
              <img src="/hero-single-father-v1-960.webp" alt="" loading="lazy" decoding="async" />
            </span>
            <span className="solution-icon"><UserRound size={25} /></span>
            <small>{text.singleEn}</small>
            <strong>{text.singleTitle}</strong>
            <p>{text.singleCopy}</p>
            <ul>{text.singlePoints.map((item) => <li key={item}><Check size={15} />{item}</li>)}</ul>
            <b>{text.select}<ArrowRight size={18} /></b>
          </button>
        </div>
      </section>

      <section className="price-section" id="price">
        <header className="decision-section-heading inverse">
          <p className="sport-kicker">{text.priceMicro}</p>
          <h2>{text.priceTitle}</h2>
          <p>{text.priceCopy}</p>
        </header>
        <div className="price-grid">
          {priceCards.map((card) => (
            <article className="price-card" key={card.country}>
              <div><span>{card.badge}</span><MapPin size={18} /></div>
              <h3>{card.country}</h3>
              <strong>{card.amount}</strong>
              <p>{card.description}</p>
            </article>
          ))}
        </div>
        <aside className="price-review">
          <div className="price-review-list">
            <strong><Scale size={20} />{text.verifyLabel}</strong>
            <ul>{text.verifyItems.map((item) => <li key={item}><Check size={15} />{item}</li>)}</ul>
          </div>
        </aside>
        <p className="price-source-note">{text.sourceNote}</p>
      </section>

      <section className="decision-answers" aria-labelledby="planning-answers-title">
        <header>
          <div><p className="sport-kicker">{text.answersMicro}</p><h2 id="planning-answers-title">{text.answersTitle}</h2></div>
          <p>{text.answersCopy}</p>
        </header>
        <div>
          {text.answers.map(([question, answer], index) => (
            <article key={question}>
              <span>0{index + 1}</span>
              <h3>{question}</h3>
              <p>{answer}</p>
            </article>
          ))}
        </div>
      </section>

      <footer className="decision-footer">
        <div className="decision-footer-privacy">
          <LockKeyhole size={22} />
          <p><strong>{text.privacyTitle}</strong>{text.privacyCopy}</p>
        </div>
        <div className="decision-footer-cta">
          <div><strong>{text.ctaTitle}</strong><small>{text.ctaCopy}</small></div>
          <button className="fatherhood-primary" type="button" onClick={onStart}>{intro.start}<ArrowRight size={19} /></button>
        </div>
      </footer>
    </section>
  );
}

function JourneyHeader({
  locale,
  active,
  onHome,
  onEdit
}: {
  locale: JourneyLocale;
  active: 2 | 3;
  onHome: () => void;
  onEdit?: () => void;
}) {
  const labels = locale === 'zh-Hans'
    ? { home: '首页', selection: '选择', recommendation: '建议', edit: '修改选择' }
    : locale === 'vi'
      ? { home: 'Trang chủ', selection: 'Lựa chọn', recommendation: 'Đề xuất', edit: 'Sửa lựa chọn' }
      : { home: 'Home', selection: 'Selection', recommendation: 'Recommendation', edit: 'Edit selections' };

  return (
    <header className="journey-header">
      <button className="journey-brand" type="button" onClick={onHome} aria-label={labels.home}>
        <img src="/brand/ivftoday-logo-640.png" alt="IVFToday" width="640" height="270" />
        <small>FATHERHOOD PLANNING</small>
      </button>
      <nav aria-label="Journey progress">
        <button type="button" onClick={onHome}><span>01</span>{labels.home}</button>
        <span className={active === 2 ? 'active' : 'complete'}><b>02</b>{labels.selection}</span>
        <span className={active === 3 ? 'active' : ''}><b>03</b>{labels.recommendation}</span>
      </nav>
      {onEdit
        ? <button className="journey-header-action" type="button" onClick={onEdit}>{labels.edit}</button>
        : <span className="journey-header-page">02 / 03</span>}
    </header>
  );
}

function SelectionPage({
  locale,
  preferences,
  setPreferences,
  goalConfirmed,
  setGoalConfirmed,
  medical,
  setMedical,
  onHome,
  onRecommend
}: {
  locale: JourneyLocale;
  preferences: UserPreferences;
  setPreferences: (preferences: UserPreferences) => void;
  goalConfirmed: boolean;
  setGoalConfirmed: (confirmed: boolean) => void;
  medical: MedicalInputs;
  setMedical: (medical: MedicalInputs) => void;
  onHome: () => void;
  onRecommend: () => void;
}) {
  const copy = uiCopy[locale];
  const categoryLabels = categoryLabelsByLocale[locale];
  const goalLabels = babyGoalLabelsByLocale[locale];
  const eggLabels = eggSourceLabelsByLocale[locale];
  const spermLabels = spermSourceLabelsByLocale[locale];
  const carrierLabels = carrierLabelsByLocale[locale];
  const pathwayLabels = pathwayLabelsByLocale[locale];
  const categories: UserCategory[] = ['male_couple', 'single_man'];
  const goals = Object.keys(goalLabels) as BabyGoal[];
  const pathway = deriveRecommendedPathway(preferences, medical);
  const pathwayInfo = pathwayLabels[pathway];
  const canRecommend = Boolean(preferences.category && goalConfirmed);
  const usesEmbryo = preferences.eggSource === 'existing_embryo';

  const text = locale === 'zh-Hans'
    ? {
        kicker: 'ONE PAGE • FOUR DECISIONS',
        title: '所有选择，在一页完成。',
        intro: '按顺序选择身份、家庭愿景与生殖资源。医疗资料为选填；完成后直接生成初步建议。',
        index: ['身份', '愿景', '资源', '资料'],
        profileTitle: '你从哪一种父亲路径开始？',
        profileCopy: '身份会影响可进入的国家与亲权文件。',
        goalTitle: '你希望规划怎样的家庭结果？',
        goalCopy: '这是愿景记录，不是性别、双胎或成功率承诺。',
        resourceTitle: '需要哪些生殖资源？',
        resourceCopy: '选择精子、卵子或胚胎，以及妊娠承载方式。',
        egg: '卵子或胚胎来源',
        sperm: '精子来源',
        carrier: '妊娠承载方式',
        derived: '当前推导路径',
        optionalTitle: '补充医疗资料（选填）',
        optionalCopy: '没有报告也可以继续。已有资料能让建议更具体，但不能代替医师判断。',
        semen: '精子活力情况',
        concentration: '精液浓度 (M/mL)',
        morphology: '精子形态 (%)',
        embryoCount: '可用冷冻胚胎数量',
        embryoLocation: '胚胎冷冻地点',
        notes: '其他需要说明的医疗背景',
        notesPlaceholder: '例如：既往精液分析、手术史、遗传筛查、传染病检测、已冻精子或既有胚胎。',
        readyTitle: '选择完成后，只生成一页建议',
        readyCopy: '建议包含匹配路径、国家筛选、预算区间、主要风险与下一步。',
        missing: '请先选择身份和家庭愿景',
        submit: '生成我的建议'
      }
    : locale === 'vi'
      ? {
          kicker: 'ONE PAGE • FOUR DECISIONS',
          title: 'Hoàn tất mọi lựa chọn trên một trang.',
          intro: 'Chọn hồ sơ, mục tiêu gia đình và nguồn sinh sản theo thứ tự. Dữ liệu y khoa là tùy chọn; sau đó nhận đề xuất sơ bộ.',
          index: ['Hồ sơ', 'Mục tiêu', 'Nguồn', 'Dữ liệu'],
          profileTitle: 'Bạn bắt đầu từ lộ trình làm cha nào?',
          profileCopy: 'Hồ sơ ảnh hưởng đến quốc gia có thể tiếp cận và giấy tờ quyền cha mẹ.',
          goalTitle: 'Bạn muốn lập kế hoạch cho kết quả gia đình nào?',
          goalCopy: 'Đây là ghi nhận mong muốn, không phải cam kết giới tính, song thai hay tỷ lệ thành công.',
          resourceTitle: 'Bạn cần những nguồn sinh sản nào?',
          resourceCopy: 'Chọn tinh trùng, trứng hoặc phôi và cách mang thai.',
          egg: 'Nguồn trứng hoặc phôi',
          sperm: 'Nguồn tinh trùng',
          carrier: 'Người mang thai',
          derived: 'Lộ trình hiện tại',
          optionalTitle: 'Bổ sung dữ liệu y khoa (tùy chọn)',
          optionalCopy: 'Bạn vẫn có thể tiếp tục khi chưa có báo cáo. Dữ liệu giúp đề xuất cụ thể hơn nhưng không thay thế bác sĩ.',
          semen: 'Tình trạng di động tinh trùng',
          concentration: 'Nồng độ tinh trùng (M/mL)',
          morphology: 'Hình thái tinh trùng (%)',
          embryoCount: 'Số phôi đông lạnh khả dụng',
          embryoLocation: 'Nơi lưu trữ phôi',
          notes: 'Bối cảnh y khoa khác',
          notesPlaceholder: 'Ví dụ: tinh dịch đồ, phẫu thuật, sàng lọc di truyền, xét nghiệm bệnh truyền nhiễm, tinh trùng hoặc phôi lưu trữ.',
          readyTitle: 'Sau khi chọn, nhận một trang đề xuất',
          readyCopy: 'Bao gồm lộ trình, quốc gia, khoảng ngân sách, rủi ro chính và bước tiếp theo.',
          missing: 'Vui lòng chọn hồ sơ và mục tiêu gia đình',
          submit: 'Tạo đề xuất của tôi'
        }
      : {
          kicker: 'ONE PAGE • FOUR DECISIONS',
          title: 'Make every selection on one page.',
          intro: 'Choose your profile, family goal, and reproductive resources in order. Medical context is optional; then generate one preliminary recommendation.',
          index: ['Profile', 'Goal', 'Resources', 'Context'],
          profileTitle: 'Which fatherhood path are you starting from?',
          profileCopy: 'Your profile affects country access and parentage documents.',
          goalTitle: 'What family outcome are you planning for?',
          goalCopy: 'This records a preference—not a promise of sex, twins, or treatment success.',
          resourceTitle: 'Which reproductive resources do you need?',
          resourceCopy: 'Choose sperm, eggs or embryos, and the gestational carrier arrangement.',
          egg: 'Egg or embryo source',
          sperm: 'Sperm source',
          carrier: 'Gestational carrier',
          derived: 'Current derived pathway',
          optionalTitle: 'Add medical context (optional)',
          optionalCopy: 'You can continue without reports. Details make the recommendation more specific but do not replace physician review.',
          semen: 'Sperm motility status',
          concentration: 'Sperm concentration (M/mL)',
          morphology: 'Sperm morphology (%)',
          embryoCount: 'Available frozen embryos',
          embryoLocation: 'Embryo storage location',
          notes: 'Other relevant medical context',
          notesPlaceholder: 'For example: prior semen analysis, surgery, genetic screening, infectious-disease testing, stored sperm, or existing embryos.',
          readyTitle: 'One completed form. One recommendation page.',
          readyCopy: 'See the matched pathway, country filter, budget range, key risks, and next actions.',
          missing: 'Choose a profile and family goal first',
          submit: 'Generate my recommendation'
        };

  const eggOptions: Array<[string, string]> = [
    ['donor', `${eggLabels.donor} | DONOR`],
    ['existing_embryo', `${eggLabels.existing_embryo} | EMBRYO`],
    ['unknown', `${eggLabels.unknown} | UNKNOWN`]
  ];
  const spermOptions: Array<[string, string]> = preferences.category === 'single_man'
    ? [
        ['self', `${spermLabels.self} | SELF`],
        ['donor', `${spermLabels.donor} | DONOR`],
        ['unknown', `${spermLabels.unknown} | UNKNOWN`]
      ]
    : [
        ['self', `${spermLabels.self} | SELF`],
        ['partner', `${spermLabels.partner} | PARTNER`],
        ['donor', `${spermLabels.donor} | DONOR`],
        ['unknown', `${spermLabels.unknown} | UNKNOWN`]
      ];
  const carrierOptions: Array<[string, string]> = [
    ['surrogate', `${carrierLabels.surrogate} | SURROGATE`],
    ['unknown', `${carrierLabels.unknown} | UNKNOWN`]
  ];

  const updatePreferences = (next: Partial<UserPreferences>) => {
    setPreferences(withDerivedPreferences({ ...preferences, ...next }, medical));
  };

  const selectGoal = (babyGoal: BabyGoal) => {
    setPreferences(withDerivedPreferences({ ...preferences, ...goalToPreferences(babyGoal) }, medical));
    setGoalConfirmed(true);
  };

  return (
    <section className="selection-journey">
      <JourneyHeader locale={locale} active={2} onHome={onHome} />
      <header className="selection-intro">
        <div>
          <p className="sport-kicker">{text.kicker}</p>
          <h1 tabIndex={-1}>{text.title}</h1>
          <p>{text.intro}</p>
        </div>
        <picture className="selection-intro-photo">
          <source
            media="(max-width: 760px)"
            srcSet={preferences.category === 'single_man'
              ? '/hero-single-father-nature-v2-960.webp'
              : '/hero-fatherhood-planning-v1-960.webp'}
          />
          <img
            src={preferences.category === 'single_man'
              ? '/hero-single-father-nature-v2.webp'
              : '/hero-fatherhood-planning-v1.webp'}
            width={preferences.category === 'single_man' ? 1536 : 1774}
            height={preferences.category === 'single_man' ? 1024 : 887}
            decoding="async"
            alt={preferences.category === 'single_man'
              ? locale === 'zh-Hans'
                ? '单身父亲在金色阳光下牵着年幼儿子的手走过自然小径'
                : locale === 'vi'
                  ? 'Người cha đơn thân nắm tay con trai nhỏ đi trên con đường thiên nhiên dưới nắng vàng'
                  : 'A single father holding his young son’s hand on a warm sunlit nature path'
              : locale === 'zh-Hans'
                ? '两位准爸爸在家中一起查看家庭规划资料'
                : locale === 'vi'
                  ? 'Hai người cha tương lai cùng xem kế hoạch gia đình tại nhà'
                  : 'Two intended fathers reviewing their family plan together at home'}
          />
        </picture>
      </header>

      <div className="selection-layout">
        <aside className="selection-index" aria-label="Selection sections">
          {text.index.map((label, index) => (
            <a key={label} href={`#selection-${index + 1}`}><span>0{index + 1}</span>{label}</a>
          ))}
        </aside>

        <div className="selection-form">
          <section className="selection-block" id="selection-1">
            <header><span>01</span><div><h2>{text.profileTitle}</h2><p>{text.profileCopy}</p></div></header>
            <div className="selection-profile-grid">
              {categories.map((category) => {
                const Icon = categoryIcons[category];
                const labels = categoryLabels[category];
                const selected = preferences.category === category;
                return (
                  <button
                    key={category}
                    className={selected ? 'selected' : ''}
                    type="button"
                    aria-pressed={selected}
                    onClick={() => setPreferences(withCategoryDefaults(preferences, category))}
                  >
                    <Icon size={22} />
                    <span><strong>{labels.title}</strong><small>{labels.en}</small></span>
                    {selected && <Check size={19} />}
                  </button>
                );
              })}
            </div>
          </section>

          <section className="selection-block" id="selection-2">
            <header><span>02</span><div><h2>{text.goalTitle}</h2><p>{text.goalCopy}</p></div></header>
            <div className="selection-goal-grid">
              {goals.map((goal) => {
                const labels = goalLabels[goal];
                const selected = goalConfirmed && preferences.babyGoal === goal;
                return (
                  <button key={goal} className={selected ? 'selected' : ''} type="button" aria-pressed={selected} onClick={() => selectGoal(goal)}>
                    <strong>{labels.title}</strong><small>{labels.en}</small>{selected && <Check size={17} />}
                  </button>
                );
              })}
            </div>
          </section>

          <section className="selection-block" id="selection-3">
            <header><span>03</span><div><h2>{text.resourceTitle}</h2><p>{text.resourceCopy}</p></div></header>
            <div className="selection-resource-grid">
              <QuestionBlock title={text.egg} en="OOCYTE / EMBRYO SOURCE">
                <Segmented
                  options={eggOptions}
                  value={preferences.eggSource}
                  onChange={(value) => updatePreferences({
                    eggSource: value as EggSource,
                    existingEmbryo: value === 'existing_embryo',
                    embryoStatus: value === 'existing_embryo' ? 'unknown' : 'none'
                  })}
                />
              </QuestionBlock>
              <QuestionBlock title={text.sperm} en="SPERM SOURCE">
                <Segmented options={spermOptions} value={preferences.spermSource} onChange={(value) => updatePreferences({ spermSource: value as SpermSource })} />
              </QuestionBlock>
              <QuestionBlock title={text.carrier} en="GESTATIONAL CARRIER">
                <Segmented options={carrierOptions} value={preferences.carrier} onChange={(value) => updatePreferences({ carrier: value as CarrierType })} />
              </QuestionBlock>
            </div>
            <div className="selection-derived">
              <span>{text.derived}</span><strong>{pathwayBadge[pathway]} · {pathwayInfo.title}</strong><p>{pathwayInfo.copy}</p>
            </div>
          </section>

          <section className="selection-block" id="selection-4">
            <header><span>04</span><div><h2>{text.optionalTitle}</h2><p>{text.optionalCopy}</p></div></header>
            {usesEmbryo && (
              <div className="selection-medical-grid">
                <NumberInput label={text.embryoCount} en="AVAILABLE EMBRYOS" value={medical.embryoCount} onChange={(embryoCount) => setMedical({ ...medical, embryoCount })} />
                <label><span>{text.embryoLocation}</span><small>STORAGE LOCATION</small><input value={medical.embryoFreezeLocation} onChange={(event) => setMedical({ ...medical, embryoFreezeLocation: event.target.value })} /></label>
              </div>
            )}
            {preferences.spermSource === 'self' || preferences.spermSource === 'partner' ? (
              <>
                <QuestionBlock title={text.semen} en="SEMEN ANALYSIS">
                  <div className="selection-motility-grid">
                    {[
                      ['normal', locale === 'zh-Hans' ? '正常 ≥40%' : locale === 'vi' ? 'Bình thường ≥40%' : 'Normal ≥40%'],
                      ['borderline', locale === 'zh-Hans' ? '偏低 20–39%' : locale === 'vi' ? 'Giảm 20–39%' : 'Reduced 20–39%'],
                      ['low', locale === 'zh-Hans' ? '较低 <20%' : locale === 'vi' ? 'Thấp <20%' : 'Low <20%'],
                      ['unknown', locale === 'zh-Hans' ? '未检测／不清楚' : locale === 'vi' ? 'Chưa rõ' : 'Untested / unknown']
                    ].map(([value, label]) => (
                      <button key={value} type="button" className={medical.spermMotility === value ? 'selected' : ''} onClick={() => setMedical({ ...medical, spermMotility: value as MedicalInputs['spermMotility'] })}>{label}</button>
                    ))}
                  </div>
                </QuestionBlock>
                <div className="selection-medical-grid">
                  <NumberInput label={text.concentration} en="SPERM CONCENTRATION" value={medical.spermConcentration} onChange={(spermConcentration) => setMedical({ ...medical, spermConcentration })} />
                  <NumberInput label={text.morphology} en="SPERM MORPHOLOGY" value={medical.spermMorphology} onChange={(spermMorphology) => setMedical({ ...medical, spermMorphology })} />
                </div>
              </>
            ) : <NoticeCard title={copy.medical.donorSpermTitle} copy={copy.medical.donorSpermCopy} />}
            <label className="selection-notes"><span>{text.notes}</span><textarea value={medical.medicalNotes} onChange={(event) => setMedical({ ...medical, medicalNotes: event.target.value })} placeholder={text.notesPlaceholder} /></label>
          </section>

          <footer className="selection-submit">
            <div><p className="sport-kicker">03 • RECOMMENDATION</p><h2>{text.readyTitle}</h2><span>{canRecommend ? text.readyCopy : text.missing}</span></div>
            <button type="button" disabled={!canRecommend} onClick={onRecommend}>{text.submit}<ArrowRight size={20} /></button>
          </footer>
        </div>
      </div>
    </section>
  );
}

function RecommendationPage({
  locale,
  preferences,
  report,
  onEdit,
  onHome
}: {
  locale: JourneyLocale;
  preferences: UserPreferences;
  report: GuidanceReport;
  onEdit: () => void;
  onHome: () => void;
}) {
  const categoryLabels = categoryLabelsByLocale[locale];
  const pathwayLabels = pathwayLabelsByLocale[locale];
  const statusLabels = countryStatusLabelsByLocale[locale];
  const validationCopy = validationCopyByLocale[locale];
  const pathwayInfo = pathwayLabels[report.recommendedPathway];
  const statusOrder: Record<GuidanceReport['countryFits'][number]['status'], number> = {
    ideal: 0,
    allowed: 0,
    needs_review: 0,
    restricted: 1,
    not_recommended: 2
  };
  const visibleCountries = [...report.countryFits].sort((left, right) =>
    statusOrder[left.status] - statusOrder[right.status] || left.code.localeCompare(right.code)
  );
  const directlyComparable = visibleCountries.filter((country) =>
    country.status === 'ideal' || country.status === 'allowed' || country.status === 'needs_review'
  );
  const comparisonCountries = directlyComparable.length
    ? directlyComparable
    : visibleCountries.filter((country) => country.status !== 'not_recommended');
  const countryPrice = (code: string, amount: number) => {
    if (code === 'US') return '$202,000–$322,000';
    if (code === 'GE') return '$81,800*';
    return formatUSD(amount);
  };
  const comparisonBudgetPoints = comparisonCountries.flatMap((country) =>
    country.code === 'US' ? [202000, 322000] : [country.averageCostUSD]
  );
  const budgetLow = comparisonBudgetPoints.length ? Math.min(...comparisonBudgetPoints) : report.costEstimate.lowUSD;
  const budgetHigh = comparisonBudgetPoints.length ? Math.max(...comparisonBudgetPoints) : report.costEstimate.highUSD;
  const comparisonCountryNames = comparisonCountries.map((country) => `${country.flag} ${country.country}`).join(' · ');
  const text = locale === 'zh-Hans'
    ? {
        kicker: 'PAGE 03 • PRELIMINARY RECOMMENDATION',
        title: '你的初步建议',
        profile: '身份',
        country: '同等比较',
        countryCount: '个可比较国家',
        noCountry: '暂无可比较国家',
        pathway: '建议路径',
        budget: '跨国预算跨度',
        budgetSource: '覆盖当前可比较国家的参考预算，不自动指定唯一国家。',
        breakdownKicker: 'ITEMIZED BUDGET',
        breakdownTitle: '预算拆解',
        breakdownCopy: '用相同费用类别比较各国报价：医疗、实验室、供卵、孕母、法律、保险、服务与差旅都应逐项核对。',
        coreBudget: '可比较国家参考跨度',
        optionalFlag: '项目',
        genericCoreNote: '左侧是统一询价项目，不应直接相加为固定套餐；右侧范围来自当前可比较国家的参考预算。',
        countryTitle: '全部国家，同一套标准',
        countryCopy: '每个国家都按身份准入、生育资源、服务范围和预算处理；排序不代表推荐强弱。',
        why: '同等比较依据',
        next: '下一步',
        caution: '需要专业复核',
        noFlags: '当前没有明显输入冲突，但仍需法律与医疗确认。',
        edit: '返回修改选择',
        home: '返回首页'
      }
    : locale === 'vi'
      ? {
          kicker: 'PAGE 03 • PRELIMINARY RECOMMENDATION',
          title: 'Đề xuất sơ bộ của bạn',
          profile: 'Hồ sơ',
          country: 'So sánh ngang nhau',
          countryCount: 'quốc gia để so sánh',
          noCountry: 'Chưa có quốc gia để so sánh',
          pathway: 'Lộ trình đề xuất',
          budget: 'Biên độ ngân sách giữa các nước',
          budgetSource: 'Bao quát ngân sách tham khảo của các quốc gia hiện có thể so sánh; không chọn sẵn một quốc gia.',
          breakdownKicker: 'ITEMIZED BUDGET',
          breakdownTitle: 'Chi tiết ngân sách',
          breakdownCopy: 'Dùng cùng nhóm chi phí để so sánh báo giá giữa các nước: y khoa, labo, trứng hiến, người mang thai, pháp lý, bảo hiểm, dịch vụ và đi lại.',
          coreBudget: 'Biên độ tham khảo giữa các nước',
          optionalFlag: 'Hạng mục',
          genericCoreNote: 'Các mục bên trái là danh mục hỏi giá chung, không phải gói cố định để cộng trực tiếp; khoảng bên phải lấy từ các quốc gia hiện có thể so sánh.',
          countryTitle: 'Mọi quốc gia, cùng một tiêu chí',
          countryCopy: 'Mỗi quốc gia được xử lý theo cùng tiêu chí về tiếp cận, nguồn sinh sản, phạm vi dịch vụ và ngân sách; thứ tự không phải xếp hạng.',
          why: 'Cơ sở so sánh ngang nhau',
          next: 'Bước tiếp theo',
          caution: 'Cần chuyên gia rà soát',
          noFlags: 'Chưa có xung đột rõ ràng, nhưng vẫn cần xác nhận pháp lý và y khoa.',
          edit: 'Quay lại sửa lựa chọn',
          home: 'Về trang chủ'
        }
      : {
          kicker: 'PAGE 03 • PRELIMINARY RECOMMENDATION',
          title: 'Your preliminary recommendation',
          profile: 'Profile',
          country: 'Equal comparison',
          countryCount: 'countries to compare',
          noCountry: 'No comparable country yet',
          pathway: 'Recommended pathway',
          budget: 'Cross-country budget span',
          budgetSource: 'Covers the current comparable-country references; no country is automatically selected.',
          breakdownKicker: 'ITEMIZED BUDGET',
          breakdownTitle: 'Price breakdown',
          breakdownCopy: 'Use the same cost categories to compare quotes across countries: medical, laboratory, donor, carrier, legal, insurance, service, and travel.',
          coreBudget: 'Comparable-country reference span',
          optionalFlag: 'Item',
          genericCoreNote: 'The left side is a common quote checklist, not a fixed package to total directly; the range on the right comes from the currently comparable countries.',
          countryTitle: 'Every country, the same criteria',
          countryCopy: 'Every country is treated with the same profile-access, reproductive-resource, service-scope, and budget rules; order is not rank.',
          why: 'Equal-comparison basis',
          next: 'Next actions',
          caution: 'Professional review required',
          noFlags: 'No obvious input conflict is present, but legal and medical confirmation is still required.',
          edit: 'Edit selections',
          home: 'Return home'
        };

  const budgetBreakdown = report.costEstimate.lines.slice(0, 6).map((line) => ({
    label: line.label,
    range: formatUSD(line.amountUSD),
    detail: line.description,
    optional: false
  }));
  const budgetCoreNote = text.genericCoreNote;

  return (
    <section className="recommendation-journey">
      <JourneyHeader locale={locale} active={3} onHome={onHome} onEdit={onEdit} />
      <header className="recommendation-hero">
        <div>
          <p className="sport-kicker">{text.kicker}</p>
          <h1 tabIndex={-1}>{text.title}</h1>
          <p>{report.summary}</p>
          <div className="recommendation-profile"><span>{text.profile}</span><strong>{preferences.category ? categoryLabels[preferences.category].title : '—'}</strong></div>
        </div>
        <picture className="recommendation-hero-photo">
          <source
            media="(max-width: 760px)"
            srcSet={preferences.category === 'single_man' ? '/hero-single-father-v1-960.webp' : '/hero-fatherhood-men-v1-960.webp'}
          />
          <img
            src={preferences.category === 'single_man' ? '/hero-single-father-v1.webp' : '/hero-fatherhood-men-v1.webp'}
            width="1774"
            height="887"
            decoding="async"
            alt={preferences.category === 'single_man'
              ? locale === 'zh-Hans' ? '单身父亲在家中抱着婴儿' : locale === 'vi' ? 'Người cha đơn thân ôm em bé tại nhà' : 'A single father holding his baby at home'
              : locale === 'zh-Hans' ? '两位父亲在家中抱着婴儿' : locale === 'vi' ? 'Hai người cha ôm em bé tại nhà' : 'Two fathers holding their baby at home'}
          />
        </picture>
      </header>

      <section className="recommendation-primary" aria-label={text.title}>
        <article><MapPin size={22} /><span>{text.country}</span><strong>{comparisonCountries.length ? `${comparisonCountries.length} ${text.countryCount}` : text.noCountry}</strong><p>{comparisonCountryNames || text.noCountry}</p></article>
        <article><ClipboardList size={22} /><span>{text.pathway}</span><strong>{pathwayInfo.title}</strong><p>{pathwayInfo.copy}</p></article>
        <article><CircleDollarSign size={22} /><span>{text.budget}</span><strong>{formatUSD(budgetLow)}–{formatUSD(budgetHigh)}</strong><p>{text.budgetSource}</p></article>
      </section>

      <section className="recommendation-budget-breakdown" aria-labelledby="budget-breakdown-title">
        <header>
          <div><p className="sport-kicker">{text.breakdownKicker}</p><h2 id="budget-breakdown-title">{text.breakdownTitle}</h2></div>
          <p>{text.breakdownCopy}</p>
        </header>
        <div className="recommendation-budget-ledger">
          <div className="recommendation-budget-lines">
            {budgetBreakdown.map((line, index) => (
              <article key={`${line.label}-${line.range}`} className={line.optional ? 'optional' : ''}>
                <div>
                  <span>{line.optional ? text.optionalFlag : `0${index + 1}`}</span>
                  <h3>{line.label}</h3>
                  <p>{line.detail}</p>
                </div>
                <strong>{line.range}</strong>
              </article>
            ))}
          </div>
          <aside>
            <small>{text.coreBudget}</small>
            <strong>{formatUSD(budgetLow)}–{formatUSD(budgetHigh)}</strong>
            <p>{budgetCoreNote}</p>
          </aside>
        </div>
      </section>

      <section className="recommendation-countries">
        <header><div><p className="sport-kicker">COUNTRY FILTER</p><h2>{text.countryTitle}</h2></div><p>{text.countryCopy}</p></header>
        <div>
          {visibleCountries.map((country) => (
            <article key={country.code}>
              <div><span>{country.flag}</span><b>{statusLabels[country.status]}</b></div>
              <h3>{country.country}<small>{country.enName}</small></h3>
              <strong>{countryPrice(country.code, country.averageCostUSD)}</strong>
              <p>{country.summary}</p>
            </article>
          ))}
        </div>
      </section>

      <section className="recommendation-actions">
        <article><h2>{text.why}</h2>{comparisonCountries.length ? comparisonCountries.map((country) => <p key={country.code}><Check size={17} />{country.flag} {country.country}: {country.reasons.join(' · ')}</p>) : <p>{text.noCountry}</p>}</article>
        <article><h2>{text.next}</h2>{report.nextSteps.map((item) => <p key={item}><ArrowRight size={17} />{item}</p>)}</article>
        <article className="recommendation-caution"><h2><Shield size={21} />{text.caution}</h2>{report.validationFlags.length ? report.validationFlags.map((flag) => <p key={flag}>{validationCopy[flag] ?? flag}</p>) : <p>{text.noFlags}</p>}<small>{report.boundaryNote}</small></article>
      </section>

      <AdvisorContactCard locale={locale} />
      <footer className="recommendation-footer">
        <button type="button" onClick={onEdit}><ArrowLeft size={19} />{text.edit}</button>
        <button type="button" onClick={onHome}>{text.home}</button>
      </footer>
    </section>
  );
}

function InfoPillar({
  icon: Icon,
  title,
  en,
  detail
}: {
  icon: typeof Activity;
  title: string;
  en: string;
  detail: string;
}) {
  return (
    <div className="pillar">
      <Icon size={24} />
      <strong>{title}</strong>
      <span>{en}</span>
      <small>{detail}</small>
    </div>
  );
}

function GoalStep({
  locale,
  preferences,
  setPreferences,
  goalConfirmed,
  setGoalConfirmed,
  onBack,
  onNext
}: {
  locale: JourneyLocale;
  preferences: UserPreferences;
  setPreferences: (preferences: UserPreferences) => void;
  goalConfirmed: boolean;
  setGoalConfirmed: (confirmed: boolean) => void;
  onBack: () => void;
  onNext: () => void;
}) {
  const copy = uiCopy[locale];
  const labelsByGoal = babyGoalLabelsByLocale[locale];
  const goals = Object.keys(labelsByGoal) as BabyGoal[];

  const selectGoal = (babyGoal: BabyGoal) => {
    const next = { ...preferences, ...goalToPreferences(babyGoal) };
    setPreferences(withDerivedPreferences(next));
    setGoalConfirmed(true);
  };

  return (
    <section className="step-screen">
      <SectionHeading
        overline={copy.goal.overline}
        title={copy.goal.title}
        en={copy.goal.en}
        copy={copy.goal.copy}
      />
      <div className="goal-grid">
        {goals.map((goal) => {
          const labels = labelsByGoal[goal];
          const selected = goalConfirmed && preferences.babyGoal === goal;
          return (
            <button
              key={goal}
              type="button"
              className={`goal-card ${selected ? 'selected' : ''}`}
              aria-pressed={selected}
              onClick={() => selectGoal(goal)}
            >
              <span>{goal === 'existing_embryo_transfer' ? <FileText size={22} /> : goal.includes('twins') ? <UsersRound size={22} /> : <Heart size={22} />}</span>
              <strong>{labels.title}</strong>
              <em>{labels.en}</em>
              <p>{labels.copy}</p>
              {selected && <b>{copy.visionSet}</b>}
            </button>
          );
        })}
      </div>
      <div className="validation-list calm goal-disclaimer">
        <p className="micro">{copy.preliminaryOnly}</p>
        <p><Shield size={16} /> {copy.goal.disclaimer}</p>
      </div>
      <FooterActions onBack={onBack} onNext={onNext} nextDisabled={!goalConfirmed} nextLabel={copy.goal.next} backLabel={copy.goal.back} />
    </section>
  );
}

function CategoryStep({
  locale,
  preferences,
  setPreferences,
  onBack,
  onNext
}: {
  locale: JourneyLocale;
  preferences: UserPreferences;
  setPreferences: (preferences: UserPreferences) => void;
  onBack: () => void;
  onNext: () => void;
}) {
  const copy = uiCopy[locale];
  const categoryLabels = categoryLabelsByLocale[locale];
  const primaryCategories: UserCategory[] = ['male_couple', 'single_man'];

  const selectCategory = (category: UserCategory) => {
    setPreferences(withCategoryDefaults(preferences, category));
  };

  return (
    <section className="step-screen">
      <SectionHeading
        overline={copy.category.overline}
        title={copy.category.title}
        en={copy.category.en}
        copy={copy.category.copy}
      />
      <div className="category-grid">
        {primaryCategories.map((category) => {
          const Icon = categoryIcons[category];
          const selected = preferences.category === category;
          const labels = categoryLabels[category];
          return (
            <button
              key={category}
              type="button"
              className={`choice-card ${selected ? 'selected' : ''}`}
              aria-pressed={selected}
              onClick={() => selectCategory(category)}
            >
              <span className="choice-visual" style={{ backgroundImage: `url(${categoryBackgrounds[category]})` }} aria-hidden="true" />
              <span className="choice-icon">
                <Icon size={24} />
              </span>
              <strong>{labels.title}</strong>
              <em>{labels.en}</em>
              <p>{labels.copy}</p>
              <small>{copy.tailored}</small>
              {selected && <b>{copy.selected}</b>}
            </button>
          );
        })}
      </div>
      <FooterActions onBack={onBack} onNext={onNext} nextDisabled={!preferences.category} nextLabel={copy.category.next} backLabel={copy.category.back} />
    </section>
  );
}

function PathStep({
  locale,
  preferences,
  setPreferences,
  onBack,
  onNext
}: {
  locale: JourneyLocale;
  preferences: UserPreferences;
  setPreferences: (preferences: UserPreferences) => void;
  onBack: () => void;
  onNext: () => void;
}) {
  const copy = uiCopy[locale];
  const eggLabels = eggSourceLabelsByLocale[locale];
  const spermLabels = spermSourceLabelsByLocale[locale];
  const carrierLabels = carrierLabelsByLocale[locale];
  const pathwayLabels = pathwayLabelsByLocale[locale];
  const validationCopy = validationCopyByLocale[locale];
  const updatePreferences = (next: Partial<UserPreferences>) => {
    const merged = { ...preferences, ...next };
    setPreferences(withDerivedPreferences(merged));
  };

  const pathway = deriveRecommendedPathway(preferences);
  const flags = getValidationFlags(preferences);
  const pathwayInfo = pathwayLabels[pathway];
  const fatherhoodProfile = preferences.category === 'male_couple' || preferences.category === 'single_man';
  const eggOptions: Array<[string, string]> = fatherhoodProfile
    ? [
        ['donor', `${eggLabels.donor} | DONOR`],
        ['existing_embryo', `${eggLabels.existing_embryo} | EMBRYO`],
        ['unknown', `${eggLabels.unknown} | UNKNOWN`]
      ]
    : [
        ['self', `${eggLabels.self} | SELF`],
        ['partner', `${eggLabels.partner} | PARTNER`],
        ['donor', `${eggLabels.donor} | DONOR`],
        ['existing_embryo', `${eggLabels.existing_embryo} | EMBRYO`],
        ['unknown', `${eggLabels.unknown} | UNKNOWN`]
      ];
  const spermOptions: Array<[string, string]> = preferences.category === 'single_man'
    ? [
        ['self', `${spermLabels.self} | SELF`],
        ['donor', `${spermLabels.donor} | DONOR`],
        ['unknown', `${spermLabels.unknown} | UNKNOWN`]
      ]
    : [
        ['self', `${spermLabels.self} | SELF`],
        ['partner', `${spermLabels.partner} | PARTNER`],
        ['donor', `${spermLabels.donor} | DONOR`],
        ['unknown', `${spermLabels.unknown} | UNKNOWN`]
      ];
  const carrierOptions: Array<[string, string]> = fatherhoodProfile
    ? [
        ['surrogate', `${carrierLabels.surrogate} | SURROGATE`],
        ['unknown', `${carrierLabels.unknown} | UNKNOWN`]
      ]
    : [
        ['self', `${carrierLabels.self} | SELF`],
        ['partner', `${carrierLabels.partner} | PARTNER`],
        ['surrogate', `${carrierLabels.surrogate} | SURROGATE`],
        ['unknown', `${carrierLabels.unknown} | UNKNOWN`]
      ];

  return (
    <section className="step-screen">
      <SectionHeading
        overline={copy.paths.overline}
        title={copy.paths.title}
        en={copy.paths.en}
        copy={copy.paths.copy}
      />
      <div className="form-panel">
        <QuestionBlock number="1" title={copy.paths.eggTitle} en="Oocyte supply source">
          <Segmented
            options={eggOptions}
            value={preferences.eggSource}
            onChange={(value) =>
              updatePreferences({
                eggSource: value as EggSource,
                existingEmbryo: preferences.babyGoal === 'existing_embryo_transfer' || value === 'existing_embryo',
                embryoStatus: value === 'existing_embryo' ? 'unknown' : 'none'
              })
            }
          />
        </QuestionBlock>

        <QuestionBlock number="2" title={copy.paths.spermTitle} en="Sperm supply source">
          <Segmented
            options={spermOptions}
            value={preferences.spermSource}
            onChange={(value) => updatePreferences({ spermSource: value as SpermSource })}
          />
        </QuestionBlock>

        <QuestionBlock number="3" title={copy.paths.carrierTitle} en="Gestational carrier">
          <Segmented
            options={carrierOptions}
            value={preferences.carrier}
            onChange={(value) => updatePreferences({ carrier: value as CarrierType })}
          />
        </QuestionBlock>

        {preferences.eggSource === 'existing_embryo' && (
          <QuestionBlock number="4" title={copy.paths.embryoTitle} en="Existing embryo status">
            <Segmented
              options={[
                ['untested', locale === 'vi' ? 'Chưa PGT | UNTESTED' : locale === 'en' ? 'Untested | UNTESTED' : '未 PGT | UNTESTED'],
                ['pgt_tested', locale === 'vi' ? 'Đã PGT | TESTED' : locale === 'en' ? 'PGT tested | TESTED' : '已 PGT | TESTED'],
                ['unknown', locale === 'vi' ? 'Chưa rõ | UNKNOWN' : locale === 'en' ? 'Unknown | UNKNOWN' : '不清楚 | UNKNOWN']
              ]}
              value={preferences.embryoStatus}
              onChange={(value) => updatePreferences({ embryoStatus: value as EmbryoStatus })}
            />
          </QuestionBlock>
        )}

        <div className="derived-path">
          <p className="micro">{copy.paths.derived}</p>
          <div>
            <span>{pathwayBadge[pathway]}</span>
            <h3>{pathwayInfo.title}</h3>
            <p>{pathwayInfo.copy}</p>
          </div>
        </div>

        {flags.length > 0 && (
          <div className="validation-list">
            <p className="micro">{copy.paths.validation}</p>
            {flags.map((flag) => (
              <p key={flag}><Shield size={16} /> {validationCopy[flag] ?? flag}</p>
            ))}
          </div>
        )}

        <AdvisorContactCard locale={locale} />
      </div>
      <FooterActions onBack={onBack} onNext={onNext} nextDisabled={!preferences.category} nextLabel={copy.paths.next} backLabel={copy.paths.back} />
    </section>
  );
}

function AdvisorContactCard({ locale }: { locale: JourneyLocale }) {
  const text = {
    eyebrow: 'ADVISOR CONTACT',
    title: locale === 'vi' ? 'Cần cố vấn kiểm tra nhanh?' : locale === 'en' ? 'Need a quick advisor check?' : '需要顾问快速确认？',
    copy: locale === 'vi'
      ? 'Nếu lộ trình, nguồn giao tử hoặc người mang thai cần đánh giá riêng, hãy liên hệ trực tiếp với cố vấn.'
      : locale === 'en'
        ? 'If pathway, donor, or carrier details need human review, contact the advisor directly.'
        : '若路径、捐赠或承载条件需要人工判断，可直接添加顾问确认。',
    qrAlt: locale === 'vi'
      ? 'Mã QR WeChat của cố vấn Tiểu Đông'
      : locale === 'en'
        ? 'Advisor Xiaodong WeChat QR code'
        : '顾问小东的微信二维码',
    qrLabel: locale === 'vi'
      ? 'Quét bằng WeChat để thêm cố vấn'
      : locale === 'en'
        ? 'Scan with WeChat to add the advisor'
        : '使用微信扫一扫添加顾问',
    qrOpen: locale === 'vi'
      ? 'Mở mã QR WeChat ở kích thước đầy đủ'
      : locale === 'en'
        ? 'Open the WeChat QR code at full size'
        : '打开完整尺寸的微信二维码'
  };

  return (
    <aside className="advisor-contact-card" aria-label={text.title}>
      <div className="advisor-contact-mark">
        <MessageCircle size={24} />
      </div>
      <div className="advisor-contact-copy">
        <p className="micro">{text.eyebrow}</p>
        <strong>{text.title}</strong>
        <span>{text.copy}</span>
      </div>
      <a
        className="advisor-contact-qr"
        href={advisorQrSrc}
        target="_blank"
        rel="noreferrer"
        aria-label={text.qrOpen}
      >
        <img
          src={advisorQrSrc}
          width="950"
          height="1345"
          loading="lazy"
          decoding="async"
          alt={text.qrAlt}
        />
        <span>{text.qrLabel}</span>
      </a>
    </aside>
  );
}

function FeasibilityStep({
  locale,
  preferences,
  medical,
  onBack,
  onNext
}: {
  locale: JourneyLocale;
  preferences: UserPreferences;
  medical: MedicalInputs;
  onBack: () => void;
  onNext: () => void;
}) {
  const copy = uiCopy[locale];
  const categoryLabels = categoryLabelsByLocale[locale];
  const pathwayLabels = pathwayLabelsByLocale[locale];
  const eggSourceLabels = eggSourceLabelsByLocale[locale];
  const spermSourceLabels = spermSourceLabelsByLocale[locale];
  const carrierLabels = carrierLabelsByLocale[locale];
  const flags = getValidationFlags(preferences);
  const pathway = deriveRecommendedPathway(preferences);
  const pathwayInfo = pathwayLabels[pathway];
  const legalFlags = flags.filter((flag) =>
    ['sex_selection_requires_country_legal_filter', 'other_lgbtq_requires_consultant_review', 'single_male_requires_surrogacy_or_review', 'male_couple_requires_surrogacy_or_review'].includes(flag)
  );
  const medicalFlags = flags.filter((flag) =>
    ['twin_preference_requires_medical_review', 'self_carrier_requires_uterus_review', 'existing_embryo_requires_embryo_data'].includes(flag)
  );
  const conflictFlags = flags.filter((flag) =>
    ['male_identity_cannot_self_provide_eggs', 'unknown_resource_requires_review', 'existing_embryo_goal_requires_embryo_source', 'unknown_goal_requires_consultant_scoping'].includes(flag)
  );
  const status = flags.length > 0 ? 'needsReview' : 'ready';
  const text = {
    unassigned: locale === 'vi' ? 'Chưa chọn' : locale === 'en' ? 'Not selected' : '未指定',
    noRequest: locale === 'vi' ? 'Không yêu cầu' : locale === 'en' ? 'Not requested' : '未要求',
    sexFilter: locale === 'vi' ? 'Cần lọc theo luật điểm đến' : locale === 'en' ? 'Requires destination legal filtering' : '需要目的地法律过滤',
    twinReview: locale === 'vi' ? 'Cần rà soát y khoa và pháp lý' : locale === 'en' ? 'Requires medical and legal review' : '需医疗与法律复核',
    surrogacyDocs: locale === 'vi' ? 'Cần giấy tờ quyền cha mẹ và ký quỹ' : locale === 'en' ? 'Requires parentage and escrow documents' : '需要亲权与信托文件',
    notMainPath: locale === 'vi' ? 'Không phải lộ trình chính' : locale === 'en' ? 'Not the main route' : '未作主路径',
    singleton: locale === 'vi' ? 'Ưu tiên an toàn đơn thai' : locale === 'en' ? 'Singleton safety is the baseline' : '优先以单胎安全为基准',
    nonSingleton: locale === 'vi' ? 'Mục tiêu không phải đơn thai cần bác sĩ rà soát' : locale === 'en' ? 'Non-singleton goal needs physician review' : '非单胎愿景需先医师复核',
    donorEgg: locale === 'vi' ? 'Trứng hiến tặng ẩn chỉ số buồng trứng tự thân' : locale === 'en' ? 'Donor egg route hides self ovarian markers' : '供卵路径隐藏自体卵巢指标',
    eggNeed: locale === 'vi' ? 'Trứng tự thân/bạn đời cần tuổi, AMH, FSH, AFC' : locale === 'en' ? 'Self/partner eggs need age, AMH, FSH, and AFC' : '自体/伴侣卵需补齐年龄、AMH、FSH、AFC',
    donorSperm: locale === 'vi' ? 'Tinh trùng hiến tặng dùng baseline của donor' : locale === 'en' ? 'Donor sperm route uses donor baseline estimate' : '捐精路径使用 donor baseline estimate',
    spermNeed: locale === 'vi' ? 'Tinh trùng tự thân/bạn đời cần nồng độ, di động, hình thái' : locale === 'en' ? 'Self/partner sperm needs concentration, motility, and morphology' : '自体/伴侣精需补齐浓度、活力、形态',
    surrogate: locale === 'vi' ? 'Mang thai hộ ẩn trường tử cung tự thân' : locale === 'en' ? 'Surrogacy route hides self uterus fields' : '孕母承载隐藏自身子宫栏位',
    carrierNeed: locale === 'vi' ? 'Tự/bạn đời mang thai cần tử cung và niêm mạc' : locale === 'en' ? 'Self/partner gestation needs uterus and endometrium data' : '自怀/伴侣怀孕需补齐子宫与内膜资料',
    embryo: locale === 'vi' ? 'Dữ liệu phôi' : locale === 'en' ? 'Embryo data' : '胚胎资料',
    notEmbryo: locale === 'vi' ? 'Không phải lộ trình phôi đã có' : locale === 'en' ? 'Not an existing embryo route' : '非既有胚胎主路径',
    pieces: locale === 'vi' ? 'phôi' : locale === 'en' ? 'embryos' : '枚'
  };

  return (
    <section className="step-screen">
      <SectionHeading
        overline={copy.review.overline}
        title={copy.review.title}
        en={copy.review.en}
        copy={copy.review.copy}
      />
      <div className="form-panel review-panel">
        <div className={`review-status ${status}`}>
          <p className="micro">{copy.review.currentStatus}</p>
          <strong>{status === 'needsReview' ? copy.review.needsReview : copy.review.ready}</strong>
          <span>{status === 'needsReview' ? copy.review.needsReviewCopy : copy.review.readyCopy}</span>
        </div>

        <div className="derived-path">
          <p className="micro">SYSTEM DERIVED TREATMENT PATH</p>
          <div>
            <span>{pathwayBadge[pathway]}</span>
            <h3>{pathwayInfo.title}</h3>
            <p>{pathwayInfo.copy}</p>
          </div>
        </div>

        <div className="review-grid">
          <ReviewCard
            locale={locale}
            icon={Scale}
            title={copy.review.legalTitle}
            en="Identity + sex selection + surrogacy + donor gametes"
            items={[
              `${copy.report.identity}: ${preferences.category ? categoryLabels[preferences.category].title : text.unassigned}`,
              `${locale === 'vi' ? 'Chọn giới tính' : locale === 'en' ? 'Sex selection' : '性别选择'}: ${preferences.sexSelectionRequested ? text.sexFilter : text.noRequest}`,
              `${locale === 'vi' ? 'Mong muốn song thai' : locale === 'en' ? 'Twin preference' : '双胎愿景'}: ${preferences.twinPreference ? text.twinReview : text.noRequest}`,
              `${locale === 'vi' ? 'Mang thai hộ' : locale === 'en' ? 'Surrogacy' : '代孕'}: ${preferences.carrier === 'surrogate' ? text.surrogacyDocs : text.notMainPath}`
            ]}
            flags={legalFlags}
          />
          <ReviewCard
            locale={locale}
            icon={Activity}
            title={copy.review.medicalTitle}
            en="Singleton preference / twin risk / egg / sperm / uterus"
            items={[
              preferences.singletonPreferred ? text.singleton : text.nonSingleton,
              preferences.eggSource === 'donor' ? text.donorEgg : text.eggNeed,
              preferences.spermSource === 'donor' ? text.donorSperm : text.spermNeed,
              preferences.carrier === 'surrogate' ? text.surrogate : text.carrierNeed
            ]}
            flags={medicalFlags}
          />
          <ReviewCard
            locale={locale}
            icon={Shield}
            title={copy.review.conflictTitle}
            en="Resource conflicts and missing plan assumptions"
            items={[
              `${copy.report.egg}: ${eggSourceLabels[preferences.eggSource]}`,
              `${copy.report.sperm}: ${spermSourceLabels[preferences.spermSource]}`,
              `${copy.report.carrier}: ${carrierLabels[preferences.carrier]}`,
              `${text.embryo}: ${preferences.existingEmbryo ? `${medical.embryoCount ?? text.unassigned} ${text.pieces} / ${preferences.embryoStatus}` : text.notEmbryo}`
            ]}
            flags={conflictFlags}
          />
        </div>
      </div>
      <FooterActions onBack={onBack} onNext={onNext} nextLabel={copy.review.next} backLabel={copy.review.back} />
    </section>
  );
}

function ReviewCard({
  locale,
  icon: Icon,
  title,
  en,
  items,
  flags
}: {
  locale: JourneyLocale;
  icon: typeof Scale;
  title: string;
  en: string;
  items: string[];
  flags: string[];
}) {
  const copy = uiCopy[locale];
  const validationCopy = validationCopyByLocale[locale];

  return (
    <article>
      <Icon size={22} />
      <h3>{title}</h3>
      <p className="micro">{en}</p>
      {items.map((item) => (
        <p key={item}><Check size={16} /> {item}</p>
      ))}
      <div className="review-flags">
        {flags.length ? flags.map((flag) => <span key={flag}>{validationCopy[flag] ?? flag}</span>) : <span>{copy.noMajorLimit}</span>}
      </div>
    </article>
  );
}

function MedicalStep({
  locale,
  preferences,
  medical,
  setMedical,
  onBack,
  onNext
}: {
  locale: JourneyLocale;
  preferences: UserPreferences;
  medical: MedicalInputs;
  setMedical: (medical: MedicalInputs) => void;
  onBack: () => void;
  onNext: () => void;
}) {
  const needsEgg = preferences.eggSource === 'self' || preferences.eggSource === 'partner';
  const needsSperm = preferences.spermSource === 'self' || preferences.spermSource === 'partner';
  const needsCarrier = preferences.carrier === 'self' || preferences.carrier === 'partner';
  const usesDonorEgg = preferences.eggSource === 'donor';
  const usesDonorSperm = preferences.spermSource === 'donor';
  const usesSurrogate = preferences.carrier === 'surrogate';
  const usesEmbryo = preferences.eggSource === 'existing_embryo';
  const copy = uiCopy[locale];
  const fatherhoodProfile = preferences.category === 'male_couple' || preferences.category === 'single_man';
  const notesPlaceholder = fatherhoodProfile
    ? locale === 'vi'
      ? 'Ví dụ: kết quả tinh dịch đồ trước đây, phẫu thuật, sàng lọc di truyền, xét nghiệm bệnh truyền nhiễm, tinh trùng lưu trữ hoặc phôi đã có.'
      : locale === 'en'
        ? 'Example: prior semen analysis, surgery, genetic screening, infectious-disease testing, stored sperm, or existing embryos.'
        : '例如：既往精液分析、手术史、遗传筛查、传染病检测、已冻精子或既有胚胎。'
    : copy.medical.notesPlaceholder;
  const field = {
    age: locale === 'vi' ? 'Tuổi người cung cấp trứng' : locale === 'en' ? 'Oocyte provider age' : '取卵者年龄',
    cycles: locale === 'vi' ? 'Số chu kỳ kích trứng IVF đã làm' : locale === 'en' ? 'Prior clinical stimulation cycles' : '之前已完成的试管取卵促排周期数',
    amh: locale === 'vi' ? 'Chỉ số AMH (ng/mL)' : locale === 'en' ? 'AMH level (ng/mL)' : 'AMH 指标 (ng/mL)',
    fsh: locale === 'vi' ? 'Chỉ số FSH (mIU/mL)' : locale === 'en' ? 'FSH level (mIU/mL)' : 'FSH 指标 (mIU/mL)',
    afc: locale === 'vi' ? 'Số nang thứ cấp AFC' : locale === 'en' ? 'Antral follicle count' : 'AFC 基础卵泡数',
    notFilled: locale === 'vi' ? 'chưa nhập' : locale === 'en' ? 'not entered' : '未填',
    embryoCount: locale === 'vi' ? 'Số phôi đông lạnh khả dụng' : locale === 'en' ? 'Available frozen embryos' : '可用冷冻胚胎数量',
    embryoPgt: locale === 'vi' ? 'Tình trạng PGT của phôi' : locale === 'en' ? 'Embryo PGT status' : '胚胎 PGT 状态',
    embryoLocation: locale === 'vi' ? 'Nơi phôi đang được đông lạnh' : locale === 'en' ? 'Frozen embryo storage location' : '胚胎冷冻地点',
    semen: locale === 'vi' ? 'Nồng độ tinh trùng (M/mL)' : locale === 'en' ? 'Sperm concentration (M/mL)' : '精液浓度 (M/mL)',
    morphology: locale === 'vi' ? 'Hình thái tinh trùng (%)' : locale === 'en' ? 'Sperm morphology (%)' : '精子形态 (%)',
    uterusNormal: locale === 'vi' ? 'Ổn định | NORMAL' : locale === 'en' ? 'Normal | NORMAL' : '稳定 | NORMAL',
    uterusReview: locale === 'vi' ? 'Cần rà soát | REVIEW' : locale === 'en' ? 'Needs review | REVIEW' : '需复核 | REVIEW',
    noUterus: locale === 'vi' ? 'Không có tử cung | NO UTERUS' : locale === 'en' ? 'No uterus | NO UTERUS' : '无子宫 | NO UTERUS',
    unknown: locale === 'vi' ? 'Chưa đánh giá | UNKNOWN' : locale === 'en' ? 'Unknown | UNKNOWN' : '未评估 | UNKNOWN',
    endometriumReady: locale === 'vi' ? 'Niêm mạc ổn định | READY' : locale === 'en' ? 'Ready | READY' : '内膜稳定 | READY',
    endometriumThin: locale === 'vi' ? 'Mỏng, cần rà soát | THIN / REVIEW' : locale === 'en' ? 'Thin / review | THIN / REVIEW' : '偏薄需复核 | THIN / REVIEW'
  };

  return (
    <section className="step-screen">
      <SectionHeading
        overline={copy.medical.overline}
        title={copy.medical.title}
        en={copy.medical.en}
        copy={copy.medical.copy}
      />
      <div className="form-panel medical-panel">
        {needsEgg && (
          <>
            <h3 className="field-section-title">
              <Activity /> {copy.medical.eggSection}
            </h3>
            <div className="two-fields">
              <NumberInput label={field.age} en="Oocyte provider age" value={medical.age} onChange={(age) => setMedical({ ...medical, age })} />
              <NumberInput label={field.cycles} en="Prior clinical stimulation cycles" value={medical.previousCycles} onChange={(previousCycles) => setMedical({ ...medical, previousCycles: previousCycles ?? 0 })} />
            </div>
            <div className="three-fields">
              <NumberInput label={field.amh} en="Anti-Mullerian hormone" value={medical.amh} onChange={(amh) => setMedical({ ...medical, amh })} step={0.1} />
              <NumberInput label={field.fsh} en="Follicle-stimulating hormone" value={medical.fsh} onChange={(fsh) => setMedical({ ...medical, fsh })} step={0.1} />
              <NumberInput label={field.afc} en="Antral follicle count" value={medical.afc} onChange={(afc) => setMedical({ ...medical, afc })} />
            </div>
            <div className="feedback-band">
              <span>{copy.medical.eggBand}</span>
              <strong>AMH {medical.amh ?? field.notFilled} ng/mL | FSH {medical.fsh ?? field.notFilled} mIU/mL | AFC {medical.afc ?? field.notFilled}</strong>
              <p>{copy.medical.eggBandCopy}</p>
            </div>
          </>
        )}

        {usesDonorEgg && <NoticeCard title={copy.medical.donorEggTitle} copy={copy.medical.donorEggCopy} />}

        {usesEmbryo && (
          <>
            <h3 className="field-section-title">
              <FileText /> {copy.medical.embryoSection}
            </h3>
            <div className="two-fields">
              <NumberInput label={field.embryoCount} en="Available frozen embryos" value={medical.embryoCount} onChange={(embryoCount) => setMedical({ ...medical, embryoCount })} />
              <label>
                <span>{field.embryoPgt}</span>
                <small>Embryo genetic testing status</small>
                <select value={preferences.embryoStatus} disabled>
                  <option value="untested">{locale === 'vi' ? 'Chưa PGT' : locale === 'en' ? 'Untested' : '未 PGT'}</option>
                  <option value="pgt_tested">{locale === 'vi' ? 'Đã PGT' : locale === 'en' ? 'PGT tested' : '已 PGT'}</option>
                  <option value="unknown">{locale === 'vi' ? 'Chưa rõ' : locale === 'en' ? 'Unknown' : '不清楚'}</option>
                  <option value="none">{locale === 'vi' ? 'Không có phôi đã có' : locale === 'en' ? 'No existing embryos' : '无既有胚胎'}</option>
                </select>
              </label>
            </div>
            <div className="two-fields">
              <label>
                <span>{field.embryoLocation}</span>
                <small>Frozen embryo storage location</small>
                <input value={medical.embryoFreezeLocation} onChange={(event) => setMedical({ ...medical, embryoFreezeLocation: event.target.value })} placeholder={copy.medical.embryoLocationPlaceholder} />
              </label>
              <label className="toggle-field">
                <input type="checkbox" checked={medical.embryoTransportReady} onChange={(event) => setMedical({ ...medical, embryoTransportReady: event.target.checked })} />
                <span>{copy.medical.embryoTransport}</span>
              </label>
            </div>
            <NoticeCard title={copy.medical.embryoNoticeTitle} copy={copy.medical.embryoNoticeCopy} />
          </>
        )}

        <hr />

        {needsSperm && (
          <QuestionBlock title={copy.medical.spermTitle} en="Seminal material motility & progression analysis">
            <div className="two-fields">
              <NumberInput label={field.semen} en="Sperm concentration" value={medical.spermConcentration} onChange={(spermConcentration) => setMedical({ ...medical, spermConcentration })} />
              <NumberInput label={field.morphology} en="Sperm morphology" value={medical.spermMorphology} onChange={(spermMorphology) => setMedical({ ...medical, spermMorphology })} />
            </div>
            <div className="path-grid four">
              {[
                ['normal', locale === 'vi' ? 'Bình thường (≥40%)' : locale === 'en' ? 'Normal (≥40%)' : '正常（活力比率 ≥40%）', 'Ideal motility for IUI / IVF'],
                ['borderline', locale === 'vi' ? 'Giảm nhẹ (20-39%)' : locale === 'en' ? 'Mildly reduced (20-39%)' : '微退化（20-39%）', 'May favor ICSI support'],
                ['low', locale === 'vi' ? 'Thấp (<20%)' : locale === 'en' ? 'Low (<20%)' : '活力受限（<20%）', 'ICSI strongly advised'],
                ['unknown', locale === 'vi' ? 'Chưa xét nghiệm / chưa rõ' : locale === 'en' ? 'Untested / unknown' : '未作检验 / 不清楚', 'Needs semen analysis']
              ].map(([value, label, copy]) => (
                <button
                  key={value}
                  type="button"
                  className={`option-card compact ${medical.spermMotility === value ? 'selected' : ''}`}
                  aria-pressed={medical.spermMotility === value}
                  onClick={() => setMedical({ ...medical, spermMotility: value as MedicalInputs['spermMotility'] })}
                >
                  <strong>{label}</strong>
                  <p>{copy}</p>
                </button>
              ))}
            </div>
          </QuestionBlock>
        )}

        {usesDonorSperm && <NoticeCard title={copy.medical.donorSpermTitle} copy={copy.medical.donorSpermCopy} />}

        {needsCarrier && (
          <>
            <hr />
            <QuestionBlock title={copy.medical.carrierTitle} en="Uterus and carrier readiness">
              <Segmented
                options={[
                  ['normal', field.uterusNormal],
                  ['abnormal', field.uterusReview],
                  ['no_uterus', field.noUterus],
                  ['unknown', field.unknown]
                ]}
                value={medical.uterusStatus}
                onChange={(value) => setMedical({ ...medical, uterusStatus: value as MedicalInputs['uterusStatus'] })}
              />
              <p className="context-note">{copy.medical.endometrium}</p>
              <Segmented
                options={[
                  ['ready', field.endometriumReady],
                  ['thin', field.endometriumThin],
                  ['unknown', field.unknown]
                ]}
                value={medical.endometriumStatus}
                onChange={(value) => setMedical({ ...medical, endometriumStatus: value as MedicalInputs['endometriumStatus'] })}
              />
              <div className="check-row">
                <label>
                  <input type="checkbox" checked={medical.previousMiscarriage} onChange={(event) => setMedical({ ...medical, previousMiscarriage: event.target.checked })} />
                  <span>{copy.medical.miscarriage}</span>
                </label>
                <label>
                  <input type="checkbox" checked={medical.previousFailedTransfer} onChange={(event) => setMedical({ ...medical, previousFailedTransfer: event.target.checked })} />
                  <span>{copy.medical.failedTransfer}</span>
                </label>
              </div>
            </QuestionBlock>
          </>
        )}

        {usesSurrogate && <NoticeCard title={copy.medical.surrogateTitle} copy={copy.medical.surrogateCopy} />}

        <label className="full-field">
          <span>{copy.medical.notes}</span>
          <textarea
            value={medical.medicalNotes}
            onChange={(event) => setMedical({ ...medical, medicalNotes: event.target.value })}
            placeholder={notesPlaceholder}
          />
        </label>
      </div>
      <FooterActions onBack={onBack} onNext={onNext} nextLabel={copy.medical.next} backLabel={copy.medical.back} />
    </section>
  );
}

function ReportStep({
  locale,
  preferences,
  medical,
  report,
  onReset
}: {
  locale: JourneyLocale;
  preferences: UserPreferences;
  medical: MedicalInputs;
  report: GuidanceReport;
  onReset: () => void;
}) {
  const [tab, setTab] = useState<ReportTab>('legality');
  const [lead, setLead] = useState<LeadRegistration>({
    representativeName: '',
    contactPhone: '',
    messenger: '',
    preferredTime: 'afternoon',
    legacyIntent: '',
    securityTier: locale === 'vi' ? 'vn_private_intake' : locale === 'en' ? 'en_private_intake' : 'two_way_nda'
  });
  const [submitted, setSubmitted] = useState(false);

  const copy = uiCopy[locale];
  const reportCopy = copy.report;
  const categoryLabels = categoryLabelsByLocale[locale];
  const babyGoalLabels = babyGoalLabelsByLocale[locale];
  const pathwayLabels = pathwayLabelsByLocale[locale];
  const eggSourceLabels = eggSourceLabelsByLocale[locale];
  const spermSourceLabels = spermSourceLabelsByLocale[locale];
  const carrierLabels = carrierLabelsByLocale[locale];
  const statusLabels = countryStatusLabelsByLocale[locale];
  const validationCopy = validationCopyByLocale[locale];
  const selectedCategory = preferences.category ? categoryLabels[preferences.category].title : locale === 'vi' ? 'Chưa chọn' : locale === 'en' ? 'Not selected' : '未指定';
  const selectedGoal = babyGoalLabels[preferences.babyGoal].title;
  const selectedCountry = report.countryFits[0];
  const pathwayInfo = pathwayLabels[report.recommendedPathway];
  const text = {
    legalityTitle: locale === 'vi' ? `Rà soát pháp lý theo ${selectedCategory}` : locale === 'en' ? `Country legal access check for ${selectedCategory}` : `针对 ${selectedCategory} 的各国司法监管核对`,
    legalityIntro: locale === 'vi'
      ? 'Luật IVF, hiến tặng và mang thai hộ khác nhau rất lớn giữa các quốc gia. Hãy chọn quốc gia có quy định rõ và bảo vệ quyền cha mẹ.'
      : locale === 'en'
        ? 'IVF, donor, and surrogacy rules vary sharply by country. Choose jurisdictions with clear access rules and parentage protection.'
        : '全球辅助求子及代孕法律有极为巨大的地域差异。请确保您在法律条文合规、出生亲权保障完善的国家进行。',
    averageCost: locale === 'vi' ? 'Chi phí tham khảo:' : locale === 'en' ? 'Reference cost:' : '参考均值开支：',
    countryDetail: locale === 'vi' ? 'phân tích đặc điểm pháp lý' : locale === 'en' ? 'legal feature analysis' : '司法特征分析',
    budgetLevel: locale === 'vi' ? 'Mức chi phí y tế và dịch vụ thường gặp' : locale === 'en' ? 'Typical clinical and agency budget level' : '医疗及代理总开支常规水平',
    around: locale === 'vi' ? 'Khoảng' : locale === 'en' ? 'Around' : '约为',
    legalNote: locale === 'vi' ? 'Điểm chính về xác lập quyền cha mẹ' : locale === 'en' ? 'Key parentage and access note' : '关键法学亲权确立条文',
    medicalTitle: locale === 'vi' ? 'Khả thi y khoa và dữ liệu cần thiết' : locale === 'en' ? 'Medical readiness and required data' : '医疗可行性与必要资料',
    medicalIntro: locale === 'vi' ? 'Các chỉ số dưới đây được tạo động theo nguồn giao tử và người mang thai đã chọn.' : locale === 'en' ? 'The following inputs are generated dynamically from the selected gamete and carrier route.' : '以下指标依据 Step 02 的配子与承载方式动态生成，不再要求不相关栏位。',
    currentInput: locale === 'vi' ? 'Tóm tắt dữ liệu y khoa hiện tại' : locale === 'en' ? 'Current medical input summary' : '目前医疗输入摘要',
    semen: locale === 'vi' ? 'Nồng độ tinh trùng' : locale === 'en' ? 'Sperm concentration' : '精液浓度',
    motility: locale === 'vi' ? 'Độ di động tinh trùng' : locale === 'en' ? 'Sperm motility' : '精子活力',
    morphology: locale === 'vi' ? 'Hình thái tinh trùng' : locale === 'en' ? 'Sperm morphology' : '精子形态',
    donorEgg: locale === 'vi' ? 'Sàng lọc người hiến trứng' : locale === 'en' ? 'Egg donor screening' : '供卵者筛查',
    donorEggSeparate: locale === 'vi' ? 'Thực hiện riêng với hồ sơ người hiến' : locale === 'en' ? 'Completed separately against the donor profile' : '依据供卵者档案另行完成',
    carrierScreening: locale === 'vi' ? 'Sàng lọc người mang thai hộ' : locale === 'en' ? 'Gestational carrier screening' : '孕母筛查',
    carrierSeparate: locale === 'vi' ? 'Thực hiện riêng với người mang thai và bác sĩ' : locale === 'en' ? 'Completed separately with the carrier and physician' : '由孕母与医师另行完成',
    embryo: locale === 'vi' ? 'Phôi đã có' : locale === 'en' ? 'Existing embryos' : '既有胚胎',
    notFilled: locale === 'vi' ? 'Chưa nhập' : locale === 'en' ? 'Not entered' : '未填',
    costTitle: locale === 'vi' ? 'Bảng hạng mục chi phí sinh sản' : locale === 'en' ? 'Itemized fertility budget breakdown' : '精算生殖流程项目一览表',
    medianBudget: locale === 'vi' ? 'Ngân sách trung vị' : locale === 'en' ? 'Median budget' : '中位常规花费总预算 (Median Budget)',
    lowBudget: locale === 'vi' ? 'Mức an toàn tối thiểu' : locale === 'en' ? 'Clinical lower bound' : '临床安全预计下限',
    highBudget: locale === 'vi' ? 'Mức dự phòng đề xuất' : locale === 'en' ? 'Prepared reserve upper band' : '预留准备金上限',
    allocation: locale === 'vi' ? 'Cấu trúc phân bổ ngân sách' : locale === 'en' ? 'Allocation structure' : '核心经费配比结构（ALLOCATION STRUCTURE）',
    timelineTitle: locale === 'vi' ? 'Mốc thực hiện y khoa, pháp lý và di chuyển' : locale === 'en' ? 'Medical, legal, and travel milestone timeline' : '预计求子里程碑、医学周期与法律公证实践日程',
    timelineIntro: locale === 'vi' ? 'Lộ trình quốc tế thường mất 10 đến 14 tháng, tùy đáp ứng kích trứng, PGT, thời gian ghép người hiến/người mang thai và giấy tờ.' : locale === 'en' ? 'A typical cross-border path takes 10 to 14 months, depending on stimulation response, PGT yield, matching wait times, and documents.' : '常规跨境为 10 至 14 个月，具体取决于初次促排效果、胚胎筛查比率、第三方匹配等待时间，以及文件办理时效。',
    riskTitle: locale === 'vi' ? 'Rà soát rủi ro và dữ liệu thiếu' : locale === 'en' ? 'Risk and missing data review' : '风险与缺失资料核对',
    riskIntro: locale === 'vi' ? 'Khu vực này chỉ liệt kê mục cần cố vấn, luật sư hoặc bác sĩ xác nhận thêm.' : locale === 'en' ? 'This area lists items that still need advisor, attorney, or physician confirmation.' : '此区域只列出需要顾问、律师或医师再次确认的项目。若没有缺失资料，仍建议保留原始检查报告供后续审核。',
    validationFlags: locale === 'vi' ? 'Các điểm cần rà soát' : locale === 'en' ? 'Validation flags' : '风险标记',
    missingDataLabel: locale === 'vi' ? 'Dữ liệu còn thiếu' : locale === 'en' ? 'Missing data' : '缺失资料',
    conflict: locale === 'vi' ? 'Xung đột / rà soát pháp lý' : locale === 'en' ? 'Conflicts / legal review' : '冲突 / 法律复核',
    missingData: locale === 'vi' ? 'Dữ liệu thiếu' : locale === 'en' ? 'Missing data' : '缺失资料',
    noConflict: locale === 'vi' ? 'Chưa phát hiện xung đột lớn.' : locale === 'en' ? 'No major route conflict found yet.' : '目前未发现重大路径冲突。',
    dataReady: locale === 'vi' ? 'Dữ liệu cần thiết cơ bản đã đủ.' : locale === 'en' ? 'Required data is basically complete.' : '目前必要资料已基本齐备。',
    registerTitle: locale === 'vi' ? 'Đăng ký tư vấn riêng IVFToday' : locale === 'en' ? 'Private IVFToday intake reservation' : '预约私密父亲路径复核',
    registerIntro: locale === 'vi' ? 'Dựa trên báo cáo sơ bộ hiện tại, cố vấn sẽ xác nhận lộ trình, quốc gia, chi phí và tài liệu tiếp theo với bạn.' : locale === 'en' ? 'Based on this preliminary blueprint, an advisor can confirm country fit, pathway, budget, and next documents one-on-one.' : '依据当前初步报告，顾问可与你一对一核对供卵、胚胎、孕母、国家准入、费用与下一批文件。',
    snapshot: locale === 'vi' ? 'Tóm tắt kế hoạch sinh sản' : locale === 'en' ? 'Parenthood blueprint snapshot' : '父亲路径蓝图概要 Snapshot',
    sealed: locale === 'vi' ? 'Hồ sơ riêng • SEALED' : locale === 'en' ? 'Private file • SEALED' : '密件印鉴 • SEALED',
    name: locale === 'vi' ? '1. Họ tên người đại diện' : locale === 'en' ? '1. Representative name' : '1. 联系人姓名',
    phone: locale === 'vi' ? '2. Số điện thoại riêng' : locale === 'en' ? '2. Private phone' : '2. 私密接听电话',
    messenger: locale === 'vi' ? '3. Zalo / WhatsApp / WeChat' : locale === 'en' ? '3. WhatsApp / WeChat' : '3. WeChat / WhatsApp',
    preferred: locale === 'vi' ? '4. Thời gian liên hệ mong muốn' : locale === 'en' ? '4. Preferred contact time' : '4. 偏好联系时段',
    intent: locale === 'vi' ? '5. Mong muốn hoặc ghi chú đặc biệt' : locale === 'en' ? '5. Family vision or special request' : '5. 父亲路径目标或特别说明',
    security: locale === 'vi' ? '6. Mức bảo mật / NDA' : locale === 'en' ? '6. Privacy / NDA security tier' : '6. 隐私加密防护安全级别',
    submitted: locale === 'vi' ? 'Đã gửi, cố vấn sẽ liên hệ riêng' : locale === 'en' ? 'Submitted, an advisor will contact you privately' : '已呈递，顾问将私密联系',
    submit: locale === 'vi' ? 'Gửi hồ sơ riêng • Đặt lịch tư vấn' : locale === 'en' ? 'Submit private file • Request advisor review' : '提交私密资料 • 申请顾问复核'
  };

  const motilityValue = medical.spermMotility === 'normal'
    ? (locale === 'vi' ? 'Bình thường' : locale === 'en' ? 'Normal' : '正常')
    : medical.spermMotility === 'borderline'
      ? (locale === 'vi' ? 'Giảm nhẹ' : locale === 'en' ? 'Mildly reduced' : '轻度下降')
      : medical.spermMotility === 'low'
        ? (locale === 'vi' ? 'Thấp' : locale === 'en' ? 'Low' : '偏低')
        : text.notFilled;
  const currentMedicalItems: Array<{ label: string; value: string }> = [];

  if (preferences.spermSource === 'self' || preferences.spermSource === 'partner') {
    currentMedicalItems.push(
      { label: text.semen, value: medical.spermConcentration === null ? text.notFilled : `${medical.spermConcentration} M/mL` },
      { label: text.motility, value: motilityValue },
      { label: text.morphology, value: medical.spermMorphology === null ? text.notFilled : `${medical.spermMorphology}%` }
    );
  }
  if (preferences.eggSource === 'donor') {
    currentMedicalItems.push({ label: text.donorEgg, value: text.donorEggSeparate });
  }
  if (preferences.carrier === 'surrogate') {
    currentMedicalItems.push({ label: text.carrierScreening, value: text.carrierSeparate });
  }
  if (preferences.eggSource === 'existing_embryo') {
    currentMedicalItems.push({ label: text.embryo, value: medical.embryoCount ? `${medical.embryoCount}` : text.notFilled });
  }

  const handleSubmit = async () => {
    if (!lead.representativeName || !lead.contactPhone) return;
    const result = await submitLead(report.id, lead, getLeadSource(locale));
    setSubmitted(result.ok);
  };

  return (
    <section className="report-screen">
      <div className="report-heading">
        <div>
          <p className="micro">{reportCopy.overline}</p>
          <h2 tabIndex={-1}>{report.summary}</h2>
          <div className="summary-tags">
            <span>{reportCopy.familyGoal}: <b>{selectedGoal}</b></span>
            <span>{reportCopy.identity}: <b>{selectedCategory}</b></span>
            <span>{reportCopy.recommended}: <b>{pathwayBadge[report.recommendedPathway]}</b></span>
            <span>{reportCopy.carrier}: <b>{carrierLabels[preferences.carrier]}</b></span>
            <span>{reportCopy.egg}: <b>{eggSourceLabels[preferences.eggSource]}</b></span>
            <span>{reportCopy.sperm}: <b>{spermSourceLabels[preferences.spermSource]}</b></span>
          </div>
        </div>
        <button type="button" onClick={onReset}>{reportCopy.reset}</button>
      </div>

      <nav className="tab-row" aria-label={locale === 'vi' ? 'Các phần của báo cáo' : locale === 'en' ? 'Report sections' : '报告章节'}>
        {reportTabs.map((item, index) => {
          const Icon = item.icon;
          return (
            <button
              key={item.id}
              type="button"
              className={tab === item.id ? 'active' : ''}
              aria-pressed={tab === item.id}
              aria-controls={tab === item.id ? `report-panel-${item.id}` : undefined}
              onClick={() => setTab(item.id)}
            >
              <Icon size={20} />
              <strong>0{index + 1} • {reportCopy.tabs[item.id][0]}</strong>
              <span>{reportCopy.tabs[item.id][1]}</span>
            </button>
          );
        })}
      </nav>
      <p className="sr-only" aria-live="polite">
        {locale === 'vi' ? 'Phần báo cáo đang mở' : locale === 'en' ? 'Open report section' : '当前报告章节'}: {reportCopy.tabs[tab][0]}
      </p>

      {tab === 'legality' && (
        <div id="report-panel-legality" className="report-panel">
          <h3><MapPin /> {text.legalityTitle}</h3>
          <p>{text.legalityIntro}</p>
          <div className="country-grid">
            {report.countryFits.map((country, index) => (
              <article key={country.code} className={`country-card ${index === 0 ? 'featured' : ''}`}>
                <div>
                  <span className="flag">{country.flag}</span>
                  <b className={country.status}>
                    {statusLabels[country.status]}
                  </b>
                </div>
                <h4>{country.country} <em>({country.enName})</em></h4>
                <p>{country.summary}</p>
                <footer>
                  <span>{text.averageCost}</span>
                  <strong>{formatUSD(country.averageCostUSD)}</strong>
                </footer>
              </article>
            ))}
          </div>
          <div className="detail-card">
            <h4>{selectedCountry.flag} {selectedCountry.country} {text.countryDetail}</h4>
            <div>
              <p><strong>{text.budgetLevel}</strong><br />{text.around} {formatUSD(selectedCountry.averageCostUSD)}</p>
              <p><strong>{text.legalNote}</strong><br />{selectedCountry.legalNote}</p>
            </div>
            <aside>
              {selectedCountry.reasons.map((reason) => (
                <span key={reason}><Check size={16} /> {reason}</span>
              ))}
            </aside>
          </div>
        </div>
      )}

      {tab === 'pathway' && (
        <div id="report-panel-pathway" className="report-panel pathway-panel">
          <h3><ClipboardList /> {reportCopy.recommended}: {pathwayInfo.title}</h3>
          <p>{pathwayInfo.copy}</p>
          <div className="pathway-summary-grid">
            <article>
              <span>{reportCopy.egg}</span>
              <strong>{eggSourceLabels[preferences.eggSource]}</strong>
              <p>{preferences.eggSource === 'donor'
                ? (locale === 'vi' ? 'Không yêu cầu AMH / FSH tự thân.' : locale === 'en' ? 'Self AMH / FSH are not required.' : '不要求填写自体 AMH / FSH。')
                : preferences.eggSource === 'existing_embryo'
                  ? (locale === 'vi' ? 'Cần bổ sung số phôi, PGT và dữ liệu vận chuyển.' : locale === 'en' ? 'Embryo count, PGT, and transport details are required.' : '需补齐胚胎数量、PGT 与转运资料。')
                  : (locale === 'vi' ? 'Cần đánh giá tuổi, AMH, FSH, AFC và lịch sử kích trứng.' : locale === 'en' ? 'Age, AMH, FSH, AFC, and prior cycles should be reviewed.' : '需评估年龄、AMH、FSH、AFC 与促排史。')}</p>
            </article>
            <article>
              <span>{reportCopy.sperm}</span>
              <strong>{spermSourceLabels[preferences.spermSource]}</strong>
              <p>{preferences.spermSource === 'donor'
                ? (locale === 'vi' ? 'Không yêu cầu báo cáo tinh dịch của khách hàng.' : locale === 'en' ? 'The user semen report is not required.' : '不要求填写使用者精液报告。')
                : (locale === 'vi' ? 'Cần xác nhận độ di động và khả năng cần ICSI.' : locale === 'en' ? 'Motility and possible ICSI support should be confirmed.' : '需确认精液活力与是否需要 ICSI。')}</p>
            </article>
            <article>
              <span>{reportCopy.carrier}</span>
              <strong>{carrierLabels[preferences.carrier]}</strong>
              <p>{preferences.carrier === 'surrogate'
                ? (locale === 'vi' ? 'Trọng tâm chuyển sang người mang thai, bảo hiểm, ký quỹ và giấy tờ quyền cha mẹ.' : locale === 'en' ? 'Focus shifts to carrier matching, insurance, escrow, and parentage documents.' : '重点转向孕母匹配、保险、信托与亲权文件。')
                : (locale === 'vi' ? 'Cần xác nhận tử cung và niêm mạc có phù hợp.' : locale === 'en' ? 'Uterus and endometrium readiness should be confirmed.' : '需确认子宫与内膜承载条件。')}</p>
            </article>
          </div>
          <div className="validation-list calm">
            <p className="micro">NEXT BEST ACTIONS</p>
            {report.nextSteps.map((step) => (
              <p key={step}><Check size={16} /> {step}</p>
            ))}
          </div>
        </div>
      )}

      {tab === 'medical' && (
        <div id="report-panel-medical" className="report-panel medical-readiness-panel">
          <h3><Activity /> {text.medicalTitle}</h3>
          <p>{text.medicalIntro}</p>
          <div className="medical-feedback-grid">
            {report.biomarkerFeedback.map((item) => (
              <article key={item.label} className={item.status}>
                <span>{item.label}</span>
                <strong>{item.value}</strong>
                <p>{item.text}</p>
                <small>{item.advice}</small>
              </article>
            ))}
          </div>
          <div className="detail-card compact-detail">
            <h4>{text.currentInput}</h4>
            <div>
              {currentMedicalItems.map((item) => (
                <p key={item.label}><strong>{item.label}</strong><br />{item.value}</p>
              ))}
            </div>
          </div>
        </div>
      )}

      {tab === 'cost' && (
        <div id="report-panel-cost" className="report-panel two-column-report">
          <div>
            <h3>{text.costTitle} <em>(Itemized Breakdown)</em></h3>
            {report.costEstimate.lines.map((line) => (
              <article className="cost-line" key={line.label}>
                <div>
                  <h4>{line.label}</h4>
                  <small>{line.category}</small>
                  <p>{line.description}</p>
                </div>
                <strong>{formatUSD(line.amountUSD)}</strong>
              </article>
            ))}
          </div>
          <aside className="budget-card">
            <p>{text.medianBudget}</p>
            <strong>{formatUSD(report.costEstimate.medianUSD)}</strong>
            <div>
              <span>{text.lowBudget} <b>{formatUSD(report.costEstimate.lowUSD)}</b></span>
              <span>{text.highBudget} <b>{formatUSD(report.costEstimate.highUSD)}</b></span>
            </div>
            <div className="donut" aria-hidden="true" />
            <small>{text.allocation}</small>
          </aside>
        </div>
      )}

      {tab === 'timeline' && (
        <div id="report-panel-timeline" className="report-panel timeline-panel">
          <h3><CalendarDays /> {text.timelineTitle}</h3>
          <p>{text.timelineIntro}</p>
          <div className="timeline-list">
            {report.timeline.map((phase) => (
              <article key={phase.phase}>
                <span><Check size={18} /></span>
                <div>
                  <small>{phase.phase} • {phase.monthRange}</small>
                  <h4>{phase.title}</h4>
                  <p>{phase.description}</p>
                </div>
              </article>
            ))}
          </div>
        </div>
      )}

      {tab === 'risk' && (
        <div id="report-panel-risk" className="report-panel risk-panel">
          <h3><Shield /> {text.riskTitle}</h3>
          <p>{text.riskIntro}</p>
          <div className="risk-grid">
            <article>
              <span>{text.validationFlags}</span>
              <h4>{text.conflict}</h4>
              {report.validationFlags.length ? (
                report.validationFlags.map((flag) => <p key={flag}><Shield size={16} /> {validationCopy[flag] ?? flag}</p>)
              ) : (
                <p><Check size={16} /> {text.noConflict}</p>
              )}
            </article>
            <article>
              <span>{text.missingDataLabel}</span>
              <h4>{text.missingData}</h4>
              {report.missingData.length ? (
                report.missingData.map((item) => <p key={item}><FileText size={16} /> {item}</p>)
              ) : (
                <p><Check size={16} /> {text.dataReady}</p>
              )}
            </article>
          </div>
        </div>
      )}

      {tab === 'register' && (
        <div id="report-panel-register" className="report-panel registration-panel">
          <h3><FileText /> {text.registerTitle}</h3>
          <p>{text.registerIntro}</p>
          <div className="registration-grid">
            <div className="snapshot-card">
              <b>{text.sealed}</b>
              <h4>{text.snapshot}</h4>
              {report.biomarkerFeedback.map((item) => (
                <p key={item.label}><span>{item.label}</span><strong>{item.value}</strong>{item.text}</p>
              ))}
              <small>{report.boundaryNote}</small>
            </div>
            <form className="lead-form" onSubmit={(event) => { event.preventDefault(); void handleSubmit(); }}>
              <label><span>{text.name}</span><input value={lead.representativeName} onChange={(event) => setLead({ ...lead, representativeName: event.target.value })} placeholder={locale === 'vi' ? 'Ví dụ: Anh Nguyễn' : locale === 'en' ? 'Example: Mr. Wang' : '例如：王先生'} /></label>
              <label><span>{text.phone}</span><input value={lead.contactPhone} onChange={(event) => setLead({ ...lead, contactPhone: event.target.value })} placeholder="+84 / +1 / +886 ..." /></label>
              <label><span>{text.messenger}</span><input value={lead.messenger} onChange={(event) => setLead({ ...lead, messenger: event.target.value })} placeholder={locale === 'vi' ? 'Không bắt buộc' : locale === 'en' ? 'Optional' : '可选填'} /></label>
              <label>
                <span>{text.preferred}</span>
                <select value={lead.preferredTime} onChange={(event) => setLead({ ...lead, preferredTime: event.target.value })}>
                  <option value="morning">{locale === 'vi' ? 'Buổi sáng' : locale === 'en' ? 'Morning private call' : '上午私密回电'}</option>
                  <option value="afternoon">{locale === 'vi' ? 'Buổi chiều' : locale === 'en' ? 'Afternoon private window' : '午后私密时段'}</option>
                  <option value="evening">{locale === 'vi' ? 'Buổi tối' : locale === 'en' ? 'Evening advisor line' : '晚间顾问专线'}</option>
                  <option value="night">{locale === 'vi' ? 'Muộn trong ngày' : locale === 'en' ? 'Late private window' : '夜半私密时段'}</option>
                </select>
              </label>
              <label className="full-field"><span>{text.intent}</span><textarea value={lead.legacyIntent} onChange={(event) => setLead({ ...lead, legacyIntent: event.target.value })} placeholder={locale === 'vi' ? 'Ví dụ: mục tiêu gia đình, ngân sách, quốc gia ưu tiên, yêu cầu PGT...' : locale === 'en' ? 'Example: family goal, budget, target country, PGT preferences, clinic matching...' : '例如：单身父亲或男同性伴侣路径、预算范围、优先国家、已有胚胎或 PGT 需求等。'} /></label>
              <label><span>{text.security}</span><select value={lead.securityTier} onChange={(event) => setLead({ ...lead, securityTier: event.target.value })}><option value="two_way_nda">{locale === 'vi' ? 'NDA hai chiều / kiểm tra bảo mật' : locale === 'en' ? 'Two-way NDA review' : '双向精准 NDA 协议合约审计'}</option><option value="family_office">{locale === 'vi' ? 'Quy trình bảo mật family office' : locale === 'en' ? 'Family office privacy workflow' : '家族办公室保密流程'}</option></select></label>
              <button type="submit" className="cinnabar-cta" aria-live="polite" disabled={!lead.representativeName || !lead.contactPhone}>{submitted ? text.submitted : text.submit}</button>
            </form>
          </div>
        </div>
      )}
    </section>
  );
}

function SectionHeading({ overline, title, en, copy }: { overline: string; title: string; en: string; copy: string }) {
  return (
    <header className="section-heading">
      <p className="micro">{overline}</p>
      <h2 tabIndex={-1}>{title}</h2>
      <em>{en}</em>
      <p>{copy}</p>
    </header>
  );
}

function QuestionBlock({ number, title, en, children }: { number?: string; title: string; en: string; children: React.ReactNode }) {
  return (
    <section className="question-block">
      <h3>{number && <span>{number}. </span>}{title}</h3>
      <p className="micro">{en}</p>
      {children}
    </section>
  );
}

function Segmented({ options, value, onChange }: { options: Array<[string, string]>; value: string; onChange: (value: string) => void }) {
  return (
    <div className="segmented">
      {options.map(([optionValue, label]) => (
        <button
          key={optionValue}
          type="button"
          className={value === optionValue ? 'selected' : ''}
          aria-pressed={value === optionValue}
          onClick={() => onChange(optionValue)}
        >
          {label}
        </button>
      ))}
    </div>
  );
}

function NumberInput({
  label,
  en,
  value,
  onChange,
  step = 1
}: {
  label: string;
  en: string;
  value: number | null;
  onChange: (value: number | null) => void;
  step?: number;
}) {
  return (
    <label>
      <span>{label}</span>
      <small>{en}</small>
      <input
        type="number"
        value={value ?? ''}
        step={step}
        onChange={(event) => onChange(event.target.value === '' ? null : Number(event.target.value))}
      />
    </label>
  );
}

function NoticeCard({ title, copy }: { title: string; copy: string }) {
  return (
    <div className="notice-card">
      <Shield size={18} />
      <div>
        <strong>{title}</strong>
        <p>{copy}</p>
      </div>
    </div>
  );
}

function FooterActions({
  onBack,
  onNext,
  nextDisabled,
  nextLabel,
  backLabel
}: {
  onBack: () => void;
  onNext: () => void;
  nextDisabled?: boolean;
  nextLabel: string;
  backLabel: string;
}) {
  return (
    <footer className="footer-actions">
      <button type="button" className="back-action" onClick={onBack}><ArrowLeft size={22} /> {backLabel}</button>
      <button type="button" className="next-action" onClick={onNext} disabled={nextDisabled}>{nextLabel} <ArrowRight size={22} /></button>
    </footer>
  );
}

interface OperationsLead extends LeadRegistration {
  id: string;
  planId: string | null;
  createdAt: string;
  status: string;
  sourceLocale?: string;
  sourceHost?: string;
  sourceChannel?: string;
  contactEmail?: string;
  consultationDate?: string;
  pathwayInterest?: string;
  countryInterest?: string;
  notes?: string;
  consentTimestamp?: string;
  consentPolicyVersion?: string;
}

interface OperationsAuditEvent {
  id: string;
  createdAt: string;
  actor: string;
  action: string;
  entityType: string;
  entityId: string | null;
  metadata: Record<string, unknown>;
}

type OperationsView = 'leads' | 'clients' | 'timeline' | 'documents' | 'risks' | 'audit' | 'settings';

const operationsNav: Array<{ id: OperationsView; label: string }> = [
  { id: 'leads', label: '线索总览' },
  { id: 'clients', label: '客户档案' },
  { id: 'timeline', label: '周期进度' },
  { id: 'documents', label: '文档清单' },
  { id: 'risks', label: '风险提示' },
  { id: 'audit', label: '审计日志' },
  { id: 'settings', label: '设置' }
];

const leadStatusLabels: Record<string, string> = {
  new: '新线索',
  contacted: '已联系',
  qualified: '已确认',
  converted: '已建档',
  closed: '已关闭'
};

const clientStatusLabels: Record<AdminClient['status'], string> = {
  new: '新建档',
  consultation: '咨询中',
  documents: '文件准备',
  cycle: '医疗周期',
  pregnancy: '妊娠阶段',
  completed: '已完成'
};

const priorityLabels: Record<AdminClient['priority'], string> = {
  standard: '标准',
  high: '高优先',
  risk_review: '风险复核'
};

const sourceLocaleLabels: Record<string, string> = {
  vi: '越南',
  en: '英文',
  'zh-Hans': '中文',
  'zh-Hant': '中文'
};

function formatDateTime(value: string) {
  if (!value) return '-';
  try {
    return new Intl.DateTimeFormat('zh-CN', {
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit'
    }).format(new Date(value));
  } catch {
    return value;
  }
}

function splitLines(value: string) {
  return value
    .split('\n')
    .map((item) => item.trim())
    .filter(Boolean);
}

function AdminPortal() {
  const [view, setView] = useState<OperationsView>('leads');
  const [clients, setClients] = useState<AdminClient[]>([]);
  const [leads, setLeads] = useState<OperationsLead[]>([]);
  const [auditEvents, setAuditEvents] = useState<OperationsAuditEvent[]>([]);
  const [selectedClientId, setSelectedClientId] = useState('');
  const [selectedLeadId, setSelectedLeadId] = useState('');
  const [query, setQuery] = useState('');
  const [loading, setLoading] = useState(true);
  const [message, setMessage] = useState('');
  const [clientDraft, setClientDraft] = useState<Partial<AdminClient>>({});

  const selectedClient = clients.find((client) => client.id === selectedClientId) ?? clients[0] ?? null;
  const selectedLead = leads.find((lead) => lead.id === selectedLeadId) ?? leads[0] ?? null;

  const loadOperations = async () => {
    setLoading(true);
    setMessage('');
    try {
      const [clientResponse, leadResponse, auditResponse] = await Promise.all([
        fetch('/api/we/clients'),
        fetch('/api/we/leads'),
        fetch('/api/we/audit')
      ]);

      if (!clientResponse.ok || !leadResponse.ok || !auditResponse.ok) {
        throw new Error('无法读取私密工作台数据');
      }

      const clientRows = (await clientResponse.json()) as AdminClient[];
      const leadRows = (await leadResponse.json()) as OperationsLead[];
      const auditRows = (await auditResponse.json()) as OperationsAuditEvent[];

      setClients(clientRows);
      setLeads(leadRows);
      setAuditEvents(auditRows);
      setSelectedClientId((current) => current || clientRows[0]?.id || '');
      setSelectedLeadId((current) => current || leadRows[0]?.id || '');
    } catch (error) {
      setMessage(error instanceof Error ? error.message : '同步失败');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    void loadOperations();
  }, []);

  useEffect(() => {
    if (selectedClient) {
      setClientDraft({
        ...selectedClient,
        documents: [...selectedClient.documents],
        risks: [...selectedClient.risks]
      });
    }
  }, [selectedClient?.id]);

  const searchText = query.toLowerCase();
  const filteredClients = clients.filter((client) =>
    `${client.id} ${client.displayName} ${client.status} ${client.priority} ${client.contactPreference} ${client.medicalSummary} ${client.documents.join(' ')} ${client.risks.join(' ')}`
      .toLowerCase()
      .includes(searchText)
  );
  const filteredLeads = leads.filter((lead) =>
    `${lead.id} ${lead.representativeName} ${lead.contactPhone} ${lead.contactEmail ?? ''} ${lead.messenger} ${lead.legacyIntent} ${lead.countryInterest ?? ''} ${lead.pathwayInterest ?? ''} ${lead.status} ${lead.sourceLocale ?? ''} ${lead.sourceHost ?? ''} ${lead.sourceChannel ?? ''}`
      .toLowerCase()
      .includes(searchText)
  );

  const saveClient = async () => {
    if (!selectedClient) return;
    setMessage('');
    const response = await fetch(`/api/we/clients/${selectedClient.id}`, {
      method: 'PATCH',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        displayName: clientDraft.displayName,
        status: clientDraft.status,
        priority: clientDraft.priority,
        contactPreference: clientDraft.contactPreference,
        medicalSummary: clientDraft.medicalSummary,
        documents: Array.isArray(clientDraft.documents) ? clientDraft.documents : [],
        risks: Array.isArray(clientDraft.risks) ? clientDraft.risks : []
      })
    });
    if (!response.ok) {
      setMessage('保存失败，请重新登录后再试');
      return;
    }
    setMessage('客户档案已保存');
    await loadOperations();
  };

  const updateLeadStatus = async (lead: OperationsLead, status: string) => {
    setMessage('');
    const response = await fetch('/api/we/leads', {
      method: 'PATCH',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ id: lead.id, status })
    });
    if (!response.ok) {
      setMessage('线索状态更新失败');
      return;
    }
    setMessage('线索状态已更新');
    await loadOperations();
  };

  const createClientFromLead = async (lead = selectedLead) => {
    if (!lead) return;
    setMessage('');
    const response = await fetch('/api/we/clients', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        displayName: lead.representativeName || `客户 ${lead.id.slice(0, 6)}`,
        profileType: 'spouses',
        status: 'consultation',
        priority: lead.securityTier === 'family_office' ? 'high' : 'standard',
        contactPreference: lead.preferredTime || '待确认',
        medicalSummary: [
          lead.legacyIntent || '由留资表单转入，等待顾问补充医疗摘要。',
          lead.contactEmail ? `邮箱：${lead.contactEmail}` : '',
          lead.contactPhone ? `电话：${lead.contactPhone}` : '',
          lead.messenger ? `通讯：${lead.messenger}` : '',
          lead.sourceHost ? `来源：${lead.sourceHost}` : '',
          lead.sourceChannel ? `渠道：${lead.sourceChannel}` : '',
          lead.consultationDate ? `咨询日期：${lead.consultationDate}` : '',
          lead.pathwayInterest ? `兴趣路径：${lead.pathwayInterest}` : '',
          lead.countryInterest ? `兴趣国家：${lead.countryInterest}` : '',
          lead.planId ? `关联方案：${lead.planId}` : ''
        ].filter(Boolean).join('\n'),
        documents: ['身份文件', '初步医疗资料', '预算与目的地偏好'],
        milestones: [],
        risks: ['待顾问确认法律准入', '待补充完整医疗资料']
      })
    });

    if (!response.ok) {
      setMessage('创建客户档案失败');
      return;
    }

    await updateLeadStatus(lead, 'converted');
    setView('clients');
  };

  const createBlankClient = async () => {
    setMessage('');
    const response = await fetch('/api/we/clients', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        displayName: `新客户 ${new Date().toLocaleDateString('zh-CN')}`,
        status: 'new',
        priority: 'standard',
        contactPreference: '待确认',
        medicalSummary: '手动创建档案，等待补充咨询记录。',
        documents: ['身份文件', '医疗资料'],
        milestones: [],
        risks: ['待首次咨询确认']
      })
    });
    if (!response.ok) {
      setMessage('创建失败');
      return;
    }
    setMessage('已创建新客户档案');
    setView('clients');
    await loadOperations();
  };

  const renderLeads = () => (
    <div className="admin-grid">
      <section className="client-table operations-table">
        <div className="table-head lead-head">
          <span>线索</span>
          <span>电话</span>
          <span>状态</span>
          <span>来源</span>
        </div>
        {filteredLeads.map((lead) => (
          <button key={lead.id} className={lead.id === selectedLead?.id ? 'selected' : ''} onClick={() => setSelectedLeadId(lead.id)}>
            <span><b>{lead.representativeName}</b><small>{lead.messenger || lead.id}</small></span>
            <span>{lead.contactPhone}</span>
            <span><b>{leadStatusLabels[lead.status] ?? lead.status}</b><small>{formatDateTime(lead.createdAt)}</small></span>
            <span><b>{sourceLocaleLabels[lead.sourceLocale ?? ''] ?? (lead.sourceLocale || '中文')}</b><small>{lead.sourceHost || lead.sourceChannel || 'ivftoday.org'}</small></span>
          </button>
        ))}
        {!filteredLeads.length && <p className="empty-state">暂无匹配线索</p>}
      </section>
      <section className="client-detail">
        <p className="micro">SELECTED LEAD</p>
        <h2>{selectedLead?.representativeName ?? '暂无线索'}</h2>
        {selectedLead && (
          <div className="detail-stack">
            <div className="detail-pair"><span>电话</span><b>{selectedLead.contactPhone}</b></div>
            <div className="detail-pair"><span>邮箱</span><b>{selectedLead.contactEmail || selectedLead.messenger || '-'}</b></div>
            <div className="detail-pair"><span>通讯方式</span><b>{selectedLead.messenger || '-'}</b></div>
            <div className="detail-pair"><span>咨询日期</span><b>{selectedLead.consultationDate || selectedLead.preferredTime || '-'}</b></div>
            <div className="detail-pair"><span>兴趣路径</span><b>{selectedLead.pathwayInterest || '-'}</b></div>
            <div className="detail-pair"><span>兴趣国家</span><b>{selectedLead.countryInterest || '-'}</b></div>
            <div className="detail-pair"><span>来源入口</span><b>{selectedLead.sourceHost || 'ivftoday.org'}</b></div>
            <div className="detail-pair"><span>地区渠道</span><b>{sourceLocaleLabels[selectedLead.sourceLocale ?? ''] ?? (selectedLead.sourceLocale || '中文')} / {selectedLead.sourceChannel || 'main-site'}</b></div>
            <div className="detail-pair"><span>联系时段</span><b>{selectedLead.preferredTime}</b></div>
            <label>
              <span>线索状态</span>
              <select value={selectedLead.status} onChange={(event) => updateLeadStatus(selectedLead, event.target.value)}>
                <option value="new">新线索</option>
                <option value="contacted">已联系</option>
                <option value="qualified">已确认</option>
                <option value="converted">已建档</option>
                <option value="closed">已关闭</option>
              </select>
            </label>
            <label><span>愿景备注</span><textarea value={selectedLead.notes || selectedLead.legacyIntent || '暂无备注'} readOnly /></label>
            <button className="next-action" type="button" onClick={() => createClientFromLead(selectedLead)}>转为客户档案</button>
          </div>
        )}
      </section>
    </div>
  );

  const renderClients = () => (
    <div className="admin-grid">
      <section className="client-table operations-table">
        <div className="table-head">
          <span>档案</span>
          <span>状态</span>
          <span>优先级</span>
          <span>更新</span>
        </div>
        {filteredClients.map((client) => (
          <button key={client.id} className={client.id === selectedClient?.id ? 'selected' : ''} onClick={() => setSelectedClientId(client.id)}>
            <span><b>{client.displayName}</b><small>{client.id}</small></span>
            <span>{clientStatusLabels[client.status]}</span>
            <span>{priorityLabels[client.priority]}</span>
            <span>{formatDateTime(client.updatedAt)}</span>
          </button>
        ))}
        {!filteredClients.length && <p className="empty-state">暂无客户档案，可从线索转入或手动创建。</p>}
      </section>
      <section className="client-detail">
        <p className="micro">SELECTED CLIENT DOSSIER</p>
        <h2>{selectedClient?.displayName ?? '暂无客户'}</h2>
        {selectedClient && (
          <div className="detail-stack">
            <label><span>客户名称</span><input value={clientDraft.displayName ?? ''} onChange={(event) => setClientDraft({ ...clientDraft, displayName: event.target.value })} /></label>
            <div className="two-mini-fields">
              <label>
                <span>状态</span>
                <select value={clientDraft.status ?? 'new'} onChange={(event) => setClientDraft({ ...clientDraft, status: event.target.value as AdminClient['status'] })}>
                  {Object.entries(clientStatusLabels).map(([value, label]) => <option key={value} value={value}>{label}</option>)}
                </select>
              </label>
              <label>
                <span>优先级</span>
                <select value={clientDraft.priority ?? 'standard'} onChange={(event) => setClientDraft({ ...clientDraft, priority: event.target.value as AdminClient['priority'] })}>
                  {Object.entries(priorityLabels).map(([value, label]) => <option key={value} value={value}>{label}</option>)}
                </select>
              </label>
            </div>
            <label><span>联系偏好</span><input value={clientDraft.contactPreference ?? ''} onChange={(event) => setClientDraft({ ...clientDraft, contactPreference: event.target.value })} /></label>
            <label><span>医疗摘要</span><textarea value={clientDraft.medicalSummary ?? ''} onChange={(event) => setClientDraft({ ...clientDraft, medicalSummary: event.target.value })} /></label>
            <label><span>文档清单（一行一项）</span><textarea value={(clientDraft.documents ?? []).join('\n')} onChange={(event) => setClientDraft({ ...clientDraft, documents: splitLines(event.target.value) })} /></label>
            <label><span>风险提示（一行一项）</span><textarea value={(clientDraft.risks ?? []).join('\n')} onChange={(event) => setClientDraft({ ...clientDraft, risks: splitLines(event.target.value) })} /></label>
            <button className="next-action" type="button" onClick={saveClient}>保存更新</button>
          </div>
        )}
      </section>
    </div>
  );

  const renderSummaryCards = (mode: OperationsView) => {
    const rows = mode === 'timeline'
      ? clients.map((client) => ({ id: client.id, title: client.displayName, meta: clientStatusLabels[client.status], items: client.milestones.length ? client.milestones.map((phase) => `${phase.title}：${phase.monthRange}`) : ['暂无里程碑，请在客户档案补充。'] }))
      : mode === 'documents'
        ? clients.map((client) => ({ id: client.id, title: client.displayName, meta: priorityLabels[client.priority], items: client.documents }))
        : clients.map((client) => ({ id: client.id, title: client.displayName, meta: priorityLabels[client.priority], items: client.risks }));

    return (
      <section className="admin-panel-wide">
        <div className="panel-title-row">
          <div>
            <p className="micro">{mode === 'timeline' ? 'CYCLE TRACKING' : mode === 'documents' ? 'DOCUMENT CONTROL' : 'RISK CONTROL'}</p>
            <h2>{mode === 'timeline' ? '周期进度' : mode === 'documents' ? '文档清单' : '风险提示'}</h2>
          </div>
          <button type="button" onClick={() => setView('clients')}>进入客户档案编辑</button>
        </div>
        <div className="operations-cards">
          {rows.map((row) => (
            <article key={row.id}>
              <small>{row.meta}</small>
              <h3>{row.title}</h3>
              {row.items.length ? row.items.map((item) => <p key={item}><Check size={16} /> {item}</p>) : <p>暂无记录</p>}
            </article>
          ))}
          {!rows.length && <p className="empty-state">暂无客户档案</p>}
        </div>
      </section>
    );
  };

  const renderAudit = () => (
    <section className="admin-panel-wide">
      <div className="panel-title-row">
        <div>
          <p className="micro">AUDIT TRAIL</p>
          <h2>审计日志</h2>
        </div>
      </div>
      <div className="audit-list">
        {auditEvents.map((event) => (
          <article key={event.id}>
            <b>{event.action}</b>
            <span>{event.entityType} / {event.entityId ?? '-'}</span>
            <small>{event.actor} · {formatDateTime(event.createdAt)}</small>
          </article>
        ))}
        {!auditEvents.length && <p className="empty-state">暂无审计记录。保存客户、更新线索后会自动记录。</p>}
      </div>
    </section>
  );

  const renderSettings = () => (
    <section className="admin-panel-wide">
      <div className="panel-title-row">
        <div>
          <p className="micro">PRIVATE CONSOLE SETTINGS</p>
          <h2>设置</h2>
        </div>
      </div>
      <div className="settings-grid">
        <article><b>访问入口</b><span>https://ivftoday.org/we</span></article>
        <article><b>线索 API</b><span>/api/we/leads</span></article>
        <article><b>客户 API</b><span>/api/we/clients</span></article>
        <article><b>通知邮箱</b><span>andrew80911@gmail.com</span></article>
      </div>
    </section>
  );

  return (
    <div className="admin-shell">
      <aside>
        <div className="admin-brand"><img src="/brand/ivftoday-logo-640.png" alt="IVFToday" width="640" height="270" /></div>
        {operationsNav.map((item) => (
          <button key={item.id} className={item.id === view ? 'active' : ''} onClick={() => setView(item.id)}>{item.label}</button>
        ))}
      </aside>
      <main>
        <header className="admin-header">
          <div>
            <p className="micro">SECURE FAMILY OFFICE OPERATIONS</p>
            <h1>私密顾问工作台</h1>
          </div>
          <div className="admin-session"><Shield size={18} /> Cloudflare Access Session</div>
        </header>
        <div className="admin-toolbar">
          <label><Search size={18} /><input value={query} onChange={(event) => setQuery(event.target.value)} placeholder="搜索客户、线索、方案或风险提示" /></label>
          <button type="button" onClick={loadOperations} disabled={loading}><RefreshCw size={18} /> {loading ? '同步中' : '同步'}</button>
          <button type="button" className="cinnabar-small" onClick={view === 'leads' && selectedLead ? () => createClientFromLead(selectedLead) : createBlankClient}>{view === 'leads' ? '转为客户' : '建立档案'}</button>
        </div>
        {message && <p className="admin-message">{message}</p>}
        {view === 'leads' && renderLeads()}
        {view === 'clients' && renderClients()}
        {view === 'timeline' && renderSummaryCards('timeline')}
        {view === 'documents' && renderSummaryCards('documents')}
        {view === 'risks' && renderSummaryCards('risks')}
        {view === 'audit' && renderAudit()}
        {view === 'settings' && renderSettings()}
      </main>
    </div>
  );
}

export default App;
